// TradeOS API Client for TradeOS Dashboard
// Integrates with TradeOS Tool API endpoints

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';

// Configuration
// Use relative URLs by default so requests go through the nginx reverse proxy.
// Only fall back to absolute URLs if explicitly set via env vars.
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
// Always use relative URLs for metrics-api so requests go through the nginx
// reverse proxy (same origin).  Direct cross-origin calls to port 18003 are
// blocked by CORS because metrics-api does not emit Access-Control-Allow-Origin.
const METRICS_API_BASE_URL = '';
const API_TIMEOUT = 45000; // 45s — backend retries up to 3×30s ≈ 36s + buffer
const ASSISTANT_API_TIMEOUT = 90000; // Venice-backed RAG answers can be slower than normal tool calls.
const ASSISTANT_STREAM_TIMEOUT = 180000; // Streaming returns an ack quickly, but deep provider calls can still run longer.

function getAuthToken(): string | null {
  return import.meta.env.VITE_API_TOKEN || useAuthStore.getState().token || localStorage.getItem('auth_token') || null;
}

/**
 * Request helper that targets the metrics-api (port 18003) instead of tradeclaw.
 * Used for /metrics/* and /agent/* endpoints on metrics-api.
 */
async function metricsRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${METRICS_API_BASE_URL}${endpoint}`;
  const defaultHeaders: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

  const config: RequestInit = { ...options, headers: { ...defaultHeaders, ...options.headers } };
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), API_TIMEOUT);
  const mergedSignal = options.signal
    ? AbortSignal.any([options.signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const response = await fetch(url, { ...config, signal: mergedSignal });
    clearTimeout(timeoutId);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new TradeOSError(response.status, errorData.detail || `HTTP error! status: ${response.status}`, errorData);
    }
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof TradeOSError) throw error;
    if (error instanceof Error && error.name === 'AbortError') {
      if (options.signal?.aborted) throw error;
      throw new TradeOSError(408, 'Request timeout');
    }
    throw new TradeOSError(0, error instanceof Error ? error.message : 'Unknown error');
  }
}

// Custom error class
class TradeOSError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'TradeOSError';
  }
}

/**
 * Extract a human-readable error message from various error response formats.
 * Handles nested objects like {detail: {message: "...", code: "..."}}
 */
function extractErrorMessage(errorData: unknown, fallbackMessage: string): string {
  if (!errorData) return fallbackMessage;
  
  // If it's a string, use it directly
  if (typeof errorData === 'string') return errorData;
  
  // If it's an object, try to extract message from common patterns
  if (typeof errorData === 'object') {
    const data = errorData as Record<string, unknown>;
    
    // Check for {detail: {message: "..."}} pattern
    if (data.detail && typeof data.detail === 'object') {
      const detail = data.detail as Record<string, unknown>;
      if (detail.message && typeof detail.message === 'string') {
        return detail.message;
      }
      if (detail.error && typeof detail.error === 'string') {
        return detail.error;
      }
      // If detail is an object without message, stringify it
      try {
        return JSON.stringify(detail);
      } catch {
        return fallbackMessage;
      }
    }
    
    // Check for {detail: "string"} pattern
    if (data.detail && typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Check for {error: "string"} pattern
    if (data.error && typeof data.error === 'string') {
      return data.error;
    }
    
    // Check for {message: "string"} pattern
    if (data.message && typeof data.message === 'string') {
      return data.message;
    }
  }
  
  return fallbackMessage;
}

// Request helper
async function request<T>(
  endpoint: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const { timeoutMs, ...requestOptions } = options;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available
  const token = getAuthToken();
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    ...requestOptions,
    headers: {
      ...defaultHeaders,
      ...requestOptions.headers,
    },
  };

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs ?? API_TIMEOUT);

  // Merge caller-supplied signal with our timeout signal so either can cancel the request.
  const mergedSignal = requestOptions.signal
    ? AbortSignal.any([requestOptions.signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const response = await fetch(url, {
      ...config,
      signal: mergedSignal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = extractErrorMessage(errorData, `HTTP error! status: ${response.status}`);
      throw new TradeOSError(response.status, errorMessage, errorData);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error instanceof TradeOSError) {
      throw error;
    }
    
    if (error instanceof Error && error.name === 'AbortError') {
      // If the caller's signal was aborted (e.g. React Query cancellation), re-throw
      // as a plain AbortError so React Query can recognise it and ignore it.
      if (requestOptions.signal?.aborted) {
        throw error;
      }
      throw new TradeOSError(408, 'Request timeout');
    }
    
    throw new TradeOSError(0, error instanceof Error ? error.message : 'Unknown error');
  }
}

// =============================================================================
// Types
// =============================================================================

export interface TradeOSStatus {
  service: string;
  kill_switch: 'on' | 'off';
  pending_intents: number;
  open_positions: number;
  orders_submitted: number;
  fills_processed: number;
}

export interface Intent {
  intent_id: string;
  symbol: string;
  side: 'buy' | 'sell';
  size?: number;
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'failed';
  confidence?: number;
  signal_type?: string;
  rationale?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Position {
  position_id?: string;
  symbol: string;
  side: 'long' | 'short';
  size: number;
  entry_price: number;
  current_price?: number;
  market_value?: number;
  unrealized_pnl?: number;
  unrealized_pnl_pct?: number;
  venue?: string;
  opened_at?: string;
  /** ADR-035: instrument class — 'futures' | 'spot' | 'equity' | 'option' | 'crypto' */
  instrument_type?: string;
  /** Strategy attribution emitted by dashboard/tool endpoints */
  strategy_type?: string;
  /** Signal attribution emitted by dashboard/tool endpoints */
  signal_type?: string;
  // Enriched fields
  entry_intent_id?: string;
  entry_fill_ids?: string[];
  exit_plan?: {
    stop_loss?: number;
    take_profit?: number;
    trailing_stop?: number;
  };
  status?: string;
  last_updated_at?: string;
  entry_signal_type?: string;
  // Closed-position fields (populated by /tool/positions/closed)
  exit_price?: number;
  realized_pnl?: number;
  realized_pnl_scope?: string;
  realized_pnl_pct?: number;
  closed_size?: number;
  close_fill_size?: number;
  close_fill_notional_usd?: number;
  notional_usd?: number;
  fees?: number;
  fees_bps?: number;
  close_reason?: string;
  close_reason_detail?: string;
  close_reason_family?: string;
  close_reason_rationale?: string;
  closed_at?: string;
  quality_tier?: string;
  holding_period_hours?: number;
}

export interface DashboardPortfolio {
  positions: Position[];
  /** Root-level total value — only present if backend emits it at root; prefer summary.total_value_usd */
  total_value?: number;
  total_value_usd?: number;
  total_pnl_usd?: number;
  total_pnl_pct?: number;
  /** Root-level 24h change — only present if backend emits it at root; prefer summary.change_24h_usd */
  change_24h?: number;
  change_24h_usd?: number;
  /** Root-level 24h change pct — only present if backend emits it at root; prefer summary.change_24h_pct */
  change_24h_pct?: number;
  /** Root-level cash balance — only present if backend emits it at root; prefer summary.cash_balance_usd */
  cash_balance_usd?: number;
  last_updated?: string;
  stale?: boolean;
  /**
   * Nested summary object — the canonical source of portfolio value/P&L from the backend.
   * Dashboard.tsx reads these with: portfolio?.summary?.change_24h_usd ?? portfolio?.change_24h_usd
   */
  summary?: {
    total_value_usd: number;
    total_pnl_usd: number;
    total_pnl_pct: number;
    change_24h_usd: number;
    change_24h_pct: number;
    /** Cash balance (equity minus open-position market value). Used by frontend to
     *  compute live portfolio value = cash_balance_usd + Σ position.unrealized_pnl */
    cash_balance_usd?: number;
  };
  allocation: {
    by_asset: Record<string, number>;
    by_strategy: Record<string, number>;
    by_venue: Record<string, number>;
  };
  performance: {
    '24h': number;
    '7d': number;
    '30d': number;
    ytd: number;
  };
}

export interface PortfolioHistory {
  timestamps: string[];
  values: number[];
}

export interface ProposeIntentRequest {
  symbol: string;
  side: 'buy' | 'sell' | 'long' | 'short';
  size?: number;
  rationale?: string;
}

export interface Order {
  order_id: string;
  intent_id?: string;
  symbol: string;
  side: 'buy' | 'sell';
  size: number;
  price?: number;
  order_type: 'market' | 'limit' | 'stop';
  status: 'pending' | 'submitted' | 'filled' | 'cancelled' | 'rejected';
  filled_size?: number;
  average_price?: number;
  venue?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PipelineHealth {
  signal_flow: 'healthy' | 'degraded' | 'unknown';
  observer_stats: Record<string, unknown>;
  best_performing: Record<string, unknown>;
  worst_performing: Record<string, unknown>;
  degradation: {
    degraded_count: number;
    degraded_types: string[];
  };
  reliability_top3: Array<{
    signal_type: string;
    reliability_scalar: number;
  }>;
  error?: string;
}

export interface ServiceHealth {
  status: 'healthy' | 'unhealthy';
  code: number | null;
  error?: string;
}

export interface DetailedHealth {
  services: Record<string, ServiceHealth>;
  healthy_count: number;
  total_count: number;
  all_healthy: boolean;
}

export interface ScannerOpportunity {
  symbol: string;
  score: number;
  signal_type: string;
  direction: 'long' | 'short';
  confidence: number;
  tradability: {
    liquidity_score: number;
    spread_pct: number;
    volume_24h: number;
  };
  metadata?: Record<string, unknown>;
}

export interface AssistantChatRequest {
  message: string;
  chat_id?: string;
  context?: Record<string, unknown>;
}

export interface AssistantAttribution {
  strategyName?: string;
  regime?: string;
  confidenceTier?: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';
}

export interface AssistantSourceDetail {
  name?: string;
  status?: string;
  source?: string;
  record_count?: number | null;
  freshness?: string | null;
  gaps?: string[];
}

export interface AssistantChatResponse {
  /** The assistant's response text */
  response: string;
  /** Action type: answer, confirm_details, reject, etc. */
  action: string;
  /** Pending action if confirmation is required */
  pending_action?: {
    id: string;
    action: string;
    symbol?: string;
    side?: string;
    size?: number;
    signal_type?: string;
    boost_scalar?: number;
    intent_id?: string;
  } | null;
  /** The persona used for this response */
  persona: string;
  /** The chat ID for this conversation */
  chat_id: string;
  /** Response timestamp */
  timestamp: string;
  /** Signal lineage stage if applicable */
  lineage_stage?: string | null;
  /** Rate limit remaining */
  rate_limit_remaining: number;
  /** Intelligence-source and opt-in learning disclosure */
  intelligence?: {
    learning_enabled: boolean;
    policy: string;
    raw_content_stored: boolean;
    stored_fields?: string[];
    sources_checked?: string[];
    source_status_counts?: Record<string, number>;
    source_details?: AssistantSourceDetail[];
    event_id?: string;
    reason?: string;
    error?: string;
    background_report_job_id?: string;
    response_profile?: 'quick' | 'standard' | 'deep_dive' | 'background_report';
    operator?: string;
    inference_provider?: string;
    sponsored_compute?: boolean;
    attribution?: {
      product?: string;
      operator?: string;
      inference_provider?: string;
      copy?: string;
    };
    routing?: {
      response_profile?: string;
      requested_model?: string;
      model?: string;
      fallback_model?: string | null;
      fallback_used?: boolean;
      provider?: string;
      max_tokens?: number;
      temperature?: number;
      reasoning_effort?: string | null;
      disable_thinking?: boolean;
      strip_thinking_response?: boolean;
      attempts?: Array<{
        model?: string;
        ok?: boolean;
        latency_ms?: number;
        error?: string;
      }>;
      usage?: Record<string, unknown>;
      llm_latency_ms?: number;
      timings_ms?: Record<string, number>;
    };
  };
}

export interface AssistantReportJobResponse {
  job_id: string;
  chat_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  response?: string | null;
  error?: string | null;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  intelligence?: AssistantChatResponse['intelligence'] | null;
}

export interface AssistantChatStreamAck {
  request_id: string;
  chat_id: string;
  response_profile?: 'quick' | 'standard' | 'deep_dive' | 'background_report';
  selected_model?: string;
  inference_provider?: string;
  operator?: string;
  timestamp?: string;
}

export interface AssistantChatStreamSources {
  request_id?: string;
  chat_id?: string;
  response_profile?: string;
  sources_checked?: string[];
  source_status_counts?: Record<string, number>;
  source_details?: AssistantSourceDetail[];
  timings_ms?: Record<string, number>;
}

export interface AssistantChatStreamProgress {
  request_id?: string;
  chat_id?: string;
  response_profile?: string;
  stage?: string;
  message?: string;
  elapsed_ms?: number;
  selected_model?: string;
  inference_provider?: string;
}

export interface AssistantChatStreamDelta {
  request_id?: string;
  chat_id?: string;
  content?: string;
}

export interface AssistantChatStreamError {
  request_id?: string;
  chat_id?: string;
  status_code?: number;
  error?: {
    code?: string;
    message?: string;
    detail?: string;
  } | string;
}

export interface AssistantChatStreamHandlers {
  onAck?: (event: AssistantChatStreamAck) => void;
  onProgress?: (event: AssistantChatStreamProgress) => void;
  onSources?: (event: AssistantChatStreamSources) => void;
  onDelta?: (event: AssistantChatStreamDelta) => void;
  onFinal?: (event: AssistantChatResponse & { request_id?: string }) => void;
  onError?: (event: AssistantChatStreamError) => void;
}

export interface AssistantHistoryItem {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  command?: Record<string, unknown>;
}

export interface AssistantHistoryResponse {
  history: AssistantHistoryItem[];
  count: number;
  ttl_seconds: number;
}

export interface AssistantExecuteResponse {
  ok: boolean;
  error?: string;
  result?: Record<string, unknown>;
}

// Execution Quality types
export interface ExecutionQualityData {
  venues: Array<{
    venue: string;
    trade_count: number;
    avg_slippage_bps: number;
    avg_fill_rate: number;
    avg_latency_ms: number;
  }>;
  algorithms: Array<{
    algorithm: string;
    trade_count: number;
    avg_slippage_bps: number;
    avg_fill_rate: number;
    avg_latency_ms: number;
  }>;
  cost_breakdown: {
    total_trades: number;
    total_slippage_bps: number;
    avg_slippage_per_trade_bps: number;
    estimated_cost_usd: number;
  };
}

export interface ExecutionLatencyData {
  overall: {
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
    avg_ms: number;
    min_ms: number;
    max_ms: number;
    sample_count: number;
  };
  per_venue: Record<string, {
    p50_ms: number;
    p95_ms: number;
    p99_ms: number;
    avg_ms: number;
    sample_count: number;
  }>;
  filtered_venue: string | null;
}

// Risk Metrics types
export interface RiskExposure {
  gross_exposure_usd: number;
  net_exposure_usd: number;
  correlated_exposure_usd: number;
  largest_position_usd?: number;
  portfolio_value_usd?: number;
  portfolio_value_known?: boolean;
  gross_exposure?: {
    usd: number;
    pct: number;
    utilization: number;
  };
  net_exposure?: {
    usd: number;
    pct: number;
    utilization: number;
    long_usd?: number;
    short_usd?: number;
  };
  limits?: {
    max_gross_exposure_pct: number;
    max_net_exposure_pct: number;
    max_position_pct: number;
    max_drawdown_pct?: number;
  };
  by_asset: Array<{
    asset: string;
    value_usd: number;
    percentage: number;
  }>;
  by_strategy: Array<{
    strategy: string;
    value_usd: number;
    percentage: number;
  }>;
  by_venue: Array<{
    venue: string;
    value_usd: number;
    percentage: number;
  }>;
  profile_limits: {
    max_gross_exposure_pct: number;
    max_net_exposure_pct: number;
    max_position_pct: number;
  };
}

export interface RiskDrawdown {
  current_drawdown_pct: number;
  current_drawdown_usd: number;
  peak_portfolio_value_usd: number;
  current_portfolio_value_usd: number;
  drawdown_scalar: number;
  profile_max_drawdown_pct: number;
  kill_switch_trigger_pct: number;
  drawdown_history: Array<{
    timestamp: string;
    drawdown_pct: number;
  }>;
  current?: {
    drawdown_pct: number;
    drawdown_usd: number;
    peak_value_usd: number;
    current_value_usd: number;
  };
  limits?: {
    max_drawdown_pct: number;
    kill_switch_threshold_pct?: number;
  };
  status?: {
    warning_level: 'normal' | 'warning' | 'critical';
    kill_switch_triggered: boolean;
    utilization?: number;
  };
  rolling_breaker?: {
    state: string;
    windows: Array<{ window: string; hwm: number; equity: number; drawdown_pct: number; threshold_pct: number; breached: boolean; warning: boolean; }>;
    worst_window: string;
    worst_drawdown_pct: number;
    trips_today: number;
    last_trip_time?: string;
    cooldown_ends?: string;
    message: string;
    is_trading_allowed: boolean;
  };
  tiered_breaker?: {
    state: string;
    drawdown_pct: number;
    tier: number;
    size_multiplier: number;
    pause_entries: boolean;
    trigger_flatten: boolean;
    requires_manual_ack: boolean;
    reason: string;
    global_pause_ends?: string | null;
    symbol_pause_ends?: string | null;
    thresholds?: {
      tier1_pct?: number;
      tier2_pct?: number;
      tier3_pct?: number;
    };
    is_trading_allowed: boolean;
    timestamp?: string;
  };
}

export interface RiskStressTest {
  scenarios: Array<{
    name: string;
    description: string;
    hypothetical_loss_usd: number;
    hypothetical_loss_pct: number;
    impact_by_asset: Array<{
      asset: string;
      loss_usd: number;
    }>;
  }>;
  last_run_at: string;
}

export interface RiskEvent {
  id?: string;  // Alias for event_id, used by Risk.tsx
  event_id: string;
  event_type: string;
  severity: 'info' | 'warning' | 'critical' | 'error';
  category: string;
  message: string;
  timestamp: string;
  symbol?: string;
  metadata?: Record<string, unknown>;
}

export interface RiskEventsResponse {
  events: RiskEvent[];
  items?: RiskEvent[];  // Alias for events, used by Risk.tsx
  next_cursor: string | null;
  has_more: boolean;
}

export type AdminBreakerAction = 'reset_counter' | 'clear_cooldown' | 'disable_with_ttl' | 're_enable';

export interface AdminBreakerItem {
  breaker_id: string;
  owner_service: string;
  breaker_family: string;
  breaker_name: string;
  stage: string;
  scope_type: string;
  scope_value: string;
  environment: string;
  state: 'ok' | 'warning' | 'triggered' | 'disabled';
  enforcement_mode: string;
  metric_value: number | null;
  threshold_value: number | null;
  unit: string | null;
  trigger_reason: string;
  plain_english_summary: string;
  impact_summary?: string;
  evidence?: Record<string, unknown>;
  cooldown_ends_at?: string | null;
  last_triggered_at?: string | null;
  is_blocking_entries: boolean;
  is_blocking_exits?: boolean;
  available_actions?: AdminBreakerAction[] | null;
  allowed_actions?: AdminBreakerAction[] | null;
  action_copy?: Partial<Record<AdminBreakerAction, string>>;
  disabled_until?: string | null;
  disable_reason?: string | null;
  requested_by?: string | null;
  reset_cutoff_at?: string | null;
  config?: Record<string, unknown>;
}

export interface AdminBreakersResponse {
  items: AdminBreakerItem[];
  count: number;
  environment: string;
  timestamp: string;
}

export interface AdminBreakerActionRequest {
  reason: string;
  requested_by?: string;
  ttl_seconds?: number;
}

export interface SystemResetRunRequest {
  reset_redis: boolean;
  reset_postgres: boolean;
  run_bootstrap: boolean;
  preserve_fills_history: boolean;
  skip_tuned_params: boolean;
  paper_seed_balance?: number | null;
}

export interface SystemResetCapabilities {
  ready: boolean;
  infra_project: string;
  main_project: string;
  redis_running: boolean;
  postgres_running: boolean;
  bootstrap_templates: Record<string, boolean>;
  missing: string[];
}

export interface SystemResetStatus {
  run_id: string | null;
  status: 'idle' | 'running' | 'succeeded' | 'failed';
  current_step: string | null;
  started_at: string | null;
  finished_at: string | null;
  exit_code: number | null;
  error: string | null;
  request: SystemResetRunRequest | null;
  logs: string[];
  capabilities: SystemResetCapabilities;
}

export interface FeasibilityRejection {
  reason: string;
  count: number;
  percentage: number;
  last_occurrence: string;
}

export interface FeasibilityRejectionsResponse {
  rejections: FeasibilityRejection[];
  period: string;
  total_rejections: number;
}

// Activity Feed types
export interface ActivityEvent {
  event_id: string;
  event_type: 'trade' | 'signal' | 'risk_event' | 'system_event' | 'order' | 'position_change' | 'kill_switch';
  severity: 'info' | 'warning' | 'critical' | 'success';
  title: string;
  description: string;
  timestamp: string;
  symbol?: string;
  value?: number;
  metadata?: Record<string, unknown>;
  acknowledged?: boolean;
}

export interface ActivityFeedResponse {
  items: ActivityEvent[];
  next_cursor: string | null;
  has_more: boolean;
  count?: number;
}

export type TradeMovieEntityType = 'intent' | 'position' | 'order' | 'trade' | 'signal';
export type TradeMovieSceneState = 'observed' | 'not_applicable' | 'data_unavailable';

export interface TradeMovieScene {
  type: string;
  timestamp: string | null;
  title: string;
  plain_english: string;
  metrics: Record<string, unknown>;
  raw: unknown;
  state: TradeMovieSceneState;
  previous_scene_type?: string | null;
  next_scene_type?: string | null;
}

export interface TradeMovieSummary {
  headline: string;
  confidence: number | null;
  confidence_tier: string;
  recommended_size_usd: number | null;
  realized_pnl_usd: number | null;
  unrealized_pnl_usd: number | null;
  attested: boolean;
  attestation_status: string;
}

export interface TradeMovieStreamRef {
  stream: string;
  entry_id: string;
  timestamp: string | null;
  matched_on: string;
}

export interface TradeMovieProvenance {
  policy_hash: string | null;
  signal_lineage: Array<{
    stage: string;
    service: string;
    timestamp: string | null;
    metadata: Record<string, unknown>;
  }>;
  stream_refs: TradeMovieStreamRef[];
  attestation: Record<string, unknown>;
  identifiers: {
    intent_id: string | null;
    position_id: string | null;
    order_id: string | null;
    trade_id: string | null;
    signal_id: string | null;
  };
}

export interface TradeMovie {
  movie_id: string;
  entity_type: TradeMovieEntityType;
  entity_id: string;
  symbol: string | null;
  direction: string | null;
  status: string | null;
  summary: TradeMovieSummary;
  scenes: TradeMovieScene[];
  provenance: TradeMovieProvenance;
}

export type CounterfactualAnchorType =
  | TradeMovieEntityType
  | 'date_range'
  | 'trading_session'
  | 'signal_type';

export interface CounterfactualAnchorRequest {
  entity_type: CounterfactualAnchorType;
  entity_id?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  session_key?: string | null;
  signal_type?: string | null;
  filters?: Record<string, unknown>;
  label?: string | null;
}

export interface CounterfactualScenarioRequest {
  type: string;
  params?: Record<string, unknown>;
  label?: string | null;
}

export interface CounterfactualRunRequest {
  anchor: CounterfactualAnchorRequest;
  scenario: CounterfactualScenarioRequest;
}

export interface CounterfactualMetricSet {
  realized_pnl_usd: number;
  max_drawdown_pct: number;
  fill_quality_bps: number;
  hold_time_minutes: number;
  fill_rate: number;
  latency_ms: number;
  trade_count: number;
  winning_trades: number;
  losing_trades: number;
  sample_size: number;
}

export interface CounterfactualWarning {
  code: string;
  message: string;
  severity: string;
}

export interface CounterfactualEvent {
  timestamp: string;
  status: string;
  message: string;
  details: Record<string, unknown>;
}

export interface CounterfactualResult {
  job_id: string;
  request_hash: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  finished_at: string | null;
  anchor: CounterfactualAnchorRequest;
  scenario: CounterfactualScenarioRequest;
  mode: 'single' | 'batch';
  baseline: CounterfactualMetricSet;
  variant: CounterfactualMetricSet;
  delta: CounterfactualMetricSet;
  explanation: string;
  warnings: CounterfactualWarning[];
  causality: 'causal' | 'best_effort';
  assumptions: string[];
  audit: Record<string, unknown>;
  error: string | null;
}

export interface CounterfactualSubmitResponse {
  job_id: string;
  status: 'running' | 'completed';
  cache_hit: boolean;
  resolved_anchor?: CounterfactualAnchorRequest;
  resolved_mode?: 'single' | 'batch';
}

export interface CounterfactualEventsResponse {
  job_id: string;
  events: CounterfactualEvent[];
}

// Market data types
export interface MarketSymbolsResponse {
  symbols: string[];
  count: number;
}

export interface SymbolStoryForecastHorizon {
  timeframe?: string;
  horizon_hours?: number;
  expected_return?: number;
  expected_return_effective?: number;
  expected_return_raw?: number;
  raw_effective_delta?: number;
  price_at_forecast?: number;
  price_expected?: number;
  price_expected_raw?: number;
  confidence?: number;
  gp_approved?: boolean;
  fresh?: boolean;
  age_seconds?: number;
  source_ts?: string;
  direction?: number;
  raw_direction?: number;
  flags?: string[];
  source?: string;
}

export interface SymbolStorySignal {
  signal_id?: string;
  recommended?: boolean;
  gate_decision?: string;
  veto_reasons?: string[];
  fusion_score?: number;
  signal_type?: string;
  direction?: number;
  fresh?: boolean;
  age_seconds?: number;
  source_ts?: string;
  forecast_abstained?: boolean;
  raw_forecast_opposes_signal?: boolean;
  horizon_policy_decision?: string;
  horizon_policy_reason?: string;
  source?: string;
}

export interface SymbolStoryBias {
  horizon_hours?: number;
  direction?: number;
  strength?: number;
  confidence?: number;
  fresh?: boolean;
  age_seconds?: number;
  source_ts?: string;
  source?: string;
}

export interface SymbolStoryNarrative {
  template_id?: string;
  headline?: string;
  plain_english?: string;
  conclusion?: string;
  why?: string[];
  conflicts?: string[];
  levels?: string[];
  what_changes?: string[];
}

export interface ForecastBoundOutcomeItem {
  forecast_bound_id?: string;
  signal_id?: string;
  decision_ts?: string;
  direction?: string;
  matched_horizon?: string;
  decision_forecast_timeframe?: string;
  target_return?: number;
  forecast_confidence?: number;
  fusion_score?: number;
  recommended?: boolean;
  gate_decision?: string;
  bias_aligned?: boolean;
  hit_4h?: boolean;
  hit_24h?: boolean;
  progress_4h?: number;
  progress_24h?: number;
  mfe_4h?: number;
  mfe_24h?: number;
  mae_4h?: number;
  mae_24h?: number;
  invalidated_before_progress?: boolean;
  final_direction_4h?: boolean;
  final_direction_24h?: boolean;
  time_to_target_hours?: number;
  forecast_bound_quality?: number;
  forecast_bound_band?: string;
  outcome_mature?: boolean;
  label_available_at?: string;
  computed_at?: string;
  source_type?: string;
}

export interface ForecastBoundQualitySummary {
  mature_count?: number;
  pending_count?: number;
  avg_quality?: number;
  quality_band?: string;
  latest_quality?: number;
  latest_band?: string;
  strong_or_partial_rate?: number;
  hit_rate_4h?: number;
  hit_rate_24h?: number;
  avg_progress_4h?: number;
  avg_progress_24h?: number;
  avg_mfe_24h?: number;
  avg_mae_24h?: number;
  invalidation_rate?: number;
  latest_decision_ts?: string;
  latest_computed_at?: string;
}

export interface ForecastBoundQualityReport {
  symbol: string;
  as_of?: string;
  lookback_days?: number;
  source?: string;
  available?: boolean;
  unavailable_reason?: string;
  summary?: ForecastBoundQualitySummary;
  latest?: ForecastBoundOutcomeItem | null;
  items?: ForecastBoundOutcomeItem[];
}

export interface ForecastBoundSignalsQualityReport {
  as_of?: string;
  source?: string;
  available?: boolean;
  unavailable_reason?: string;
  matched_count?: number;
  mature_count?: number;
  items_by_signal_id?: Record<string, ForecastBoundOutcomeItem>;
  items?: ForecastBoundOutcomeItem[];
}

export interface ForecastBoundAggregateItem {
  signal_id?: string;
  aggregate_key?: string;
  scope?: string;
  symbol?: string;
  signal_type?: string;
  regime?: string;
  matched_horizon?: string;
  decision_forecast_timeframe?: string;
  lookback_days?: number;
  sample_start_ts?: string;
  sample_end_ts?: string;
  mature_rows?: number;
  mature_clusters?: number;
  avg_quality?: number;
  quality_band?: string;
  strong_or_partial_rate?: number;
  hit_rate_4h?: number;
  hit_rate_24h?: number;
  avg_progress_4h?: number;
  avg_progress_24h?: number;
  avg_mfe_24h?: number;
  avg_mae_24h?: number;
  invalidation_rate?: number;
  latest_decision_ts?: string;
  readiness_scope?: string;
  ready?: boolean;
  readiness_reason?: string;
  confidence_cap?: number;
  refreshed_at?: string;
  metadata_jsonb?: Record<string, unknown>;
}

export interface ForecastBoundAggregateSummary {
  aggregate_count?: number;
  ready_count?: number;
  best_scope?: string;
  best_ready?: boolean;
  best_readiness_reason?: string;
  best_quality_band?: string;
  best_mature_clusters?: number;
}

export interface ForecastBoundAggregateReport {
  as_of?: string;
  source?: string;
  available?: boolean;
  unavailable_reason?: string;
  symbol?: string | null;
  signal_type?: string | null;
  regime?: string | null;
  scope?: string | null;
  matched_horizon?: string | null;
  decision_forecast_timeframe?: string | null;
  lookback_days?: number;
  summary?: ForecastBoundAggregateSummary;
  best?: ForecastBoundAggregateItem | null;
  items?: ForecastBoundAggregateItem[];
}

export interface ForecastBoundSignalsAggregateReport {
  as_of?: string;
  source?: string;
  available?: boolean;
  unavailable_reason?: string;
  lookback_days?: number;
  matched_count?: number;
  aggregate_count?: number;
  ready_count?: number;
  items_by_signal_id?: Record<string, ForecastBoundAggregateItem>;
  items?: ForecastBoundAggregateItem[];
}

export interface ForecastBoundQualityTotals {
  total_count?: number;
  mature_count?: number;
  pending_count?: number;
  mature_symbol_count?: number;
  aggregate_count?: number;
  ready_count?: number;
  ready_symbol_count?: number;
  aggregate_count_all_lookbacks?: number;
  ready_count_all_lookbacks?: number;
  ready_symbol_count_all_lookbacks?: number;
  latest_mature_decision_ts?: string;
  latest_aggregate_refreshed_at?: string;
  shadow_mode_enabled?: boolean;
  live_modifier_enabled?: boolean;
}

export interface ForecastBoundQualitySummaryReport {
  as_of?: string;
  source?: string;
  available?: boolean;
  unavailable_reason?: string;
  lookback_days?: number;
  summary?: ForecastBoundQualityTotals;
}

export interface SymbolStoryForecastBound {
  available?: boolean;
  lookback_days?: number;
  quality?: number;
  band?: string;
  mature_count?: number;
  pending_count?: number;
  strong_or_partial_rate?: number;
  hit_rate_4h?: number;
  hit_rate_24h?: number;
  avg_progress_4h?: number;
  avg_progress_24h?: number;
  avg_mfe_24h?: number;
  avg_mae_24h?: number;
  invalidation_rate?: number;
  latest_signal_id?: string;
  latest_decision_ts?: string;
  latest_computed_at?: string;
  fresh?: boolean;
  age_seconds?: number;
  source_ts?: string;
  source?: string;
}

export interface SymbolStoryResponse {
  story_schema_version: string;
  story_id: string;
  symbol: string;
  as_of: string;
  verdict: string;
  actionability: 'trade' | 'watch' | 'avoid' | 'risk_off' | 'unknown' | string;
  summary: string;
  summary_template_id?: string;
  watch_levels: Record<string, number | string | boolean | null>;
  forecast_story: Record<string, SymbolStoryForecastHorizon>;
  signal_story: SymbolStorySignal;
  bias_story: Record<string, SymbolStoryBias>;
  risk_flags: string[];
  what_changes_verdict?: string[];
  microstructure_story?: Record<string, unknown>;
  market_context?: Record<string, unknown>;
  outcomes?: Record<string, unknown>;
  forecast_bound_story?: SymbolStoryForecastBound;
  source_freshness?: Record<string, unknown>;
  narrative?: SymbolStoryNarrative;
  evidence_fingerprint: string;
}

export interface MarketTrade {
  trade_id: string;
  timestamp: string;
  symbol: string;
  side: string;
  /** Position action context: open, add, reduce, close */
  action?: string;
  size: number;
  price: number;
  venue: string;
  paper_trading?: boolean;
  tier?: number;
  signal_id?: string;
  commission?: number;
  strategy_id?: string;
  profile_id?: string;
}

export interface MarketTradesResponse {
  items: MarketTrade[];
  symbol: string;
  count: number;
}

/**
 * NT-bridge fill from /api/dashboard/nt-fills.
 * paper_trading=true when the fill came from a simulated venue (NT_TIER=1).
 */
export interface NtFill {
  fill_id: string;
  signal_id: string;
  symbol: string;
  side: string;
  /** Position action context: open, add, reduce, close */
  action?: string;
  quantity: number;
  price: number;
  commission: number;
  venue: string;
  strategy_id: string;
  profile_id: string;
  timestamp_ms: number;
  paper_trading: boolean;
  tier: number;
}

export interface NtFillsResponse {
  items: NtFill[];
  count: number;
  paper_trading_mode: boolean;
}

/**
 * Executed trade from /api/trades.
 * Represents a completed fill (closed or partially closed position).
 */
export interface ExecutedTrade {
  id: string;
  orderId: string;
  symbol: string;
  side: 'buy' | 'sell';
  /** Position action context: open, add, reduce, close */
  action?: string;
  quantity: number;
  price: number;
  fee: number;
  executedAt: string;
  /** Exit attribution emitted for reduce/close fills when available */
  positionId?: string;
  entryPrice?: number;
  exitPrice?: number;
  closedQty?: number;
  closedNotionalUsd?: number;
  remainingQty?: number;
  realizedPnl?: number;
  realizedPnlPct?: number;
  realizedPnlScope?: string;
  realizedPnlCumulative?: number;
  exitReason?: string;
  exitReasonDetail?: string;
  exitReasonFamily?: string;
}

export interface ExecutedTradesResponse {
  items: ExecutedTrade[];
  total: number;
}

export interface MarketTickerResponse {
  symbol: string;
  price: number | null;
  source: string;
  /** 24-hour price change percentage; present when backend emits it */
  change_pct_24h?: number;
}

export interface VPINData {
  level: 'normal' | 'caution' | 'defensive' | 'emergency';
  z_score: number;
  bucket_volume: number;
  time_window: string;
}

export interface StablecoinHealth {
  usdt: {
    peg: number;
    deviation_bps: number;
    status: 'healthy' | 'warning' | 'critical';
  };
  usdc: {
    peg: number;
    deviation_bps: number;
    status: 'healthy' | 'warning' | 'critical';
  };
  dai: {
    peg: number;
    deviation_bps: number;
    status: 'healthy' | 'warning' | 'critical';
  };
}

// Signal Accuracy types
export interface SignalAccuracy {
  signal_type: string;
  total_signals: number;
  winning_signals: number;
  losing_signals: number;
  win_rate: number;
  avg_pnl_bps: number;
  total_pnl_bps: number;
  /** USD avg PnL per trade derived from realized position closes; null when no closed positions yet */
  avg_pnl_usd: number | null;
  /** USD total PnL derived from realized position closes */
  total_pnl_usd: number;
  /** null when fewer than 2 realized outcomes exist */
  sharpe_ratio: number | null;
  profit_factor: number;
  /** Computed from the per-signal equity curve; 0 when no closed positions */
  max_drawdown_pct: number;
  /** Average holding time in hours; 0 when no entry/exit timestamps yet */
  avg_holding_hours: number;
  confidence: number;
  last_signal_at: string;
  window_start: string;
  window_end: string;
  avg_win_bps: number;
  avg_loss_bps: number;
  /** ADR-065 P2: Pearson correlation of composite_score vs pnl_r; null if <20 observations */
  ic_value: number | null;
  /** Number of [score, pnl_r] pairs in the rolling IC buffer */
  ic_sample_count: number;
  /** ADR-065 P2: isotonic calibration state for this signal_type */
  calibration_status: 'active' | 'cold_start' | 'disabled';
}

export interface AccuracyResponse {
  accuracy: SignalAccuracy[];
  period: string;
  generated_at: string;
}

export interface EAEntryQualityReadinessSummary {
  cohorts_considered: number;
  phase3_ready_count: number;
  phase4_ready_count: number;
  exit_policy_review_count: number;
  blocked_count: number;
  scope_types: string[];
}

export interface EAEntryQualityOperationalState {
  ready: boolean;
  blockers: string[];
  producer_stats: Record<string, number | string | boolean | null>;
}

export interface EAEntryQualityCohortMetrics {
  sample_count: number;
  live_sample_count: number;
  backfill_sample_count: number;
  coverage_ratio: number;
  counterfactual_pending_rate: number;
  entry_quality_mean: number;
  expectancy_bps: number;
  profit_factor: number;
  mfe_capture_mean: number;
  proper_exit_rate: number;
  hard_stop_rate: number;
  time_stop_no_followthrough_rate: number;
  no_mfe_invalidation_rate: number;
  post_exit_validation_rate: number;
  premature_exit_rate: number;
  horizon_mismatch_rate: number;
  correct_exit_then_reentry_rate: number;
  late_unattributed_move_rate: number;
  missed_followthrough_bps_mean: number;
  exit_maturity_mean: number;
  wilson_lower_bound: number;
}

export interface EAEntryQualityShadowAdjustment {
  confidence_delta: number;
  fusion_delta: number;
  threshold_add: number;
  size_multiplier: number;
}

export interface EAEntryQualityCohort {
  scope_hash: string;
  scope_key: string;
  scope_type: string;
  dimensions: Record<string, string>;
  readiness_level: 'phase4_ready' | 'phase3_ready' | 'exit_policy_review' | 'reentry_review' | 'watch' | 'blocked' | string;
  recommended_action: string;
  phase3_ready: boolean;
  phase4_ready: boolean;
  phase3_blockers: string[];
  phase4_blockers: string[];
  shadow_candidate: string;
  shadow_reason: string;
  notes: string[];
  metrics: EAEntryQualityCohortMetrics;
  proposed_shadow_adjustment: EAEntryQualityShadowAdjustment;
}

export interface EAEntryQualityRecentDiagnostics {
  window_hours: number;
  generated_at: string;
  available: boolean;
  recent?: {
    closed_count: number;
    winner_count: number;
    loser_count: number;
    realized_pnl_usd: number;
    label_count: number;
    live_label_count: number;
    early_adverse_count: number;
    early_adverse_rate: number;
    avg_entry_quality: number;
    avg_exit_quality: number;
    actual_edge_bps_sum: number;
    counterfactual_pending_rate: number;
    post_exit_validation_rate: number;
    premature_exit_count: number;
    horizon_mismatch_count: number;
  };
  loss_buckets: Array<{
    bucket: string;
    sample_count: number;
    avg_entry_quality: number;
    actual_edge_bps_sum: number;
    early_adverse_count: number;
  }>;
  worst_signal_types: Array<{
    signal_type: string;
    sample_count: number;
    avg_entry_quality: number;
    avg_exit_quality: number;
    actual_edge_bps_sum: number;
    hard_stop_rate: number;
    early_adverse_count: number;
  }>;
  error?: string;
}

export interface EAEntryQualityDailyLossBreaker {
  daily_pnl: number;
  daily_loss_abs_usd: number;
  limit_usd: number;
  triggered: boolean;
  trigger_reason?: string;
  data_source?: string;
  closed_count?: number;
  winner_count?: number;
  loser_count?: number;
  period_start?: string;
}

export interface EAEntryQualityReadinessReport {
  report_version: string;
  label_version: string;
  generated_at: string;
  config: Record<string, number | string | boolean>;
  operational: EAEntryQualityOperationalState;
  summary: EAEntryQualityReadinessSummary;
  phase3_ready: EAEntryQualityCohort[];
  phase4_ready: EAEntryQualityCohort[];
  exit_policy_review: EAEntryQualityCohort[];
  blocked: EAEntryQualityCohort[];
  cohorts: EAEntryQualityCohort[];
  diagnostics?: EAEntryQualityRecentDiagnostics;
  daily_loss_breaker?: EAEntryQualityDailyLossBreaker;
  error?: string;
}

/** Per-symbol directional bias accuracy from the calibration endpoint */
export interface SymbolBiasAccuracy {
  symbol: string;
  accuracy: number;   // fraction [0,1] — correct directional predictions / total
  correct: number;
  total: number;
}

// =============================================================================
// Post-Feasibility Intent Signal types
// =============================================================================

/**
 * IntentSignal represents a post-feasibility intent from the `intents` Redis stream.
 * These have passed ALL quality gates: balance, risk limits, TTL, opposing signals,
 * cost model, Kelly sizing, drawdown scalar, venue health, policy hash, correlated
 * positions. APPROVED intents are the highest-quality signal surface in the system.
 */
export interface IntentSignal {
  id: string;                         // Redis stream entry ID
  intent_id: string;
  signal_id: string;
  symbol: string;
  direction: 'long' | 'short';
  verdict: 'APPROVED' | 'REJECTED' | 'SIZED_DOWN';
  reasons: string[];                  // Parsed from JSON string
  sizing_adjustment: number;          // 1.0 = no change, <1.0 = sized down
  recommended_size_usd: number;
  signal_type: string;
  confidence: number;
  strategy_id: string;
  created_at: string;
  modifications?: Record<string, unknown>;
  stages_completed?: string[];
  signal_lineage?: string;
  entry_reject_reason?: string;
  fusion_recommended?: string;
  /** vol-regime-adjusted threshold used by signal-fusion at decision time (ENH-2) */
  applied_threshold?: number;
  /** ATR/hold-scaled minimum net return floor from signal-fusion (ENH-6 + ADR-084-v2) */
  min_net_threshold?: number;
}

export interface IntentsResponse {
  items: IntentSignal[];
  total: number;
  next_cursor: string | null;
  filters: {
    verdict: string | null;
    symbol: string | null;
    signal_type: string | null;
    fusion_recommended_only?: boolean;
  };
  error?: string;
}

// =============================================================================
// Fusion Signal types — pre-feasibility, post-scoring signals from signal-fusion
// =============================================================================

/**
 * FusionSignal represents a scored (but not yet gated) signal from the
 * signal-fusion service.  These exist in Redis `signals.fused:latest:{symbol}`
 * keys and arrive BEFORE the feasibility layer creates an IntentSignal.
 * Viewing these lets you see what the fusion engine is evaluating before
 * any gate (risk, Kelly, drawdown, venue health, etc.) filters them out.
 */
export interface FusionSignal {
  symbol: string;
  signal_id: string;
  signal_type?: string;                 // upstream generator/strategy family
  strategy?: string;                    // fallback alias from signal-fusion
  signal_ts: string;                  // ISO datetime
  original_direction: number;         // +1 = long, -1 = short
  original_confidence: number;
  signal_timeframe: string;           // e.g. "4h", "1h"

  matched_horizon: string;            // e.g. "h4"
  horizon_hours: number;
  hold_estimate_hours: number;
  hold_estimate_label: string;

  fusion_score: number;               // 0–1 composite score
  recommended: boolean;               // final recommendation verdict from signal-fusion
  accuracy_score: number;
  rr_score: number;
  timing_score: number;

  conf_calibrated: number;
  bias_direction: number;
  bias_strength: number;
  bias_aligned: boolean;
  bias_alignment_factor: number;

  expected_return: number;
  expected_edge_bps?: number;
  vol_1sigma: number;
  rr_ratio: number;
  price_at_forecast: number;
  price_expected: number;
  price_low_68: number;
  price_high_68: number;

  win_rate: number;
  freshness_decay: number;

  // ── Adaptive-threshold envelope (debug/audit trail) ──────────────────────
  // These are the LIVE values used at decision time, after vol-regime adjustment.
  // applied_threshold: vol-regime-adjusted recommend_threshold (ENH-2)
  // min_net_threshold: ATR/hold-scaled minimum net return floor (ENH-6 + ADR-084-v2)
  applied_threshold: number;          // vol-regime adjusted score threshold
  min_net_threshold: number;          // ATR/hold-scaled min net return floor
  fusion_confidence?: number;
  proactive_confidence?: number;
  expected_return_effective?: number;
  expected_return_raw?: number;
  sizing_scalar?: number;

  forecast_abstained?: boolean;
  degraded_mode_override?: boolean;
  degraded_mode_reason?: string;
  raw_forecast_opposes_signal?: boolean;
  forecast_path?: string;
  forecast_at?: string;
  forecast_timeframe?: string;
  horizon_policy_version?: string;
  horizon_policy_decision?: string;
  horizon_policy_reason?: string;
  horizon_policy_decision_forecast_alignment?: string;
  horizon_policy_decision_bias_alignment?: string;
  horizon_policy_timing_alignment?: string;
  horizon_policy_timing_forecast_alignment?: string;
  horizon_policy_timing_bias_alignment?: string;
  native_signal_timeframe?: string;
  decision_forecast_timeframe?: string;
  decision_forecast_at?: string;
  decision_forecast_hash?: string;
  decision_bias_horizon_hours?: number;
  decision_bias_direction?: number;
  decision_bias_strength?: number;
  blessing_forecast_timeframe?: string;
  blessing_forecast_at?: string;
  blessing_direction?: number;
  blessing_confidence?: number;
  blessing_alignment?: string;
  timing_forecast_timeframe?: string;
  timing_direction?: number;
  timing_bias_strength?: number;
  timing_confidence?: number;

  forecast_bound_shadow_enabled?: boolean;
  forecast_bound_shadow_available?: boolean;
  forecast_bound_shadow_ready?: boolean;
  forecast_bound_shadow_source?: string;
  forecast_bound_shadow_aggregate_key?: string;
  forecast_bound_shadow_scope?: string;
  forecast_bound_shadow_band?: string;
  forecast_bound_shadow_quality?: number;
  forecast_bound_shadow_mature_rows?: number;
  forecast_bound_shadow_mature_clusters?: number;
  forecast_bound_shadow_readiness_reason?: string;
  forecast_bound_shadow_confidence_cap?: number;
  forecast_bound_shadow_modifier_candidate?: string;
  forecast_bound_shadow_lookback_days?: number;
  forecast_bound_shadow_invalidation_rate?: number;
  forecast_bound_shadow_strong_or_partial_rate?: number;
  forecast_bound_shadow_hit_rate_24h?: number;
  forecast_bound_shadow_progress_24h?: number;
  forecast_bound_live_modifier_enabled?: boolean;
  forecast_bound_live_modifier_effect?: number;

  market_pulse_enabled?: boolean | string;
  market_pulse_available?: boolean | string;
  market_pulse_horizon_hours?: number | string;
  market_pulse_sector?: string;
  market_pulse_global_label?: string;
  market_pulse_sector_label?: string;
  market_pulse_global_score?: number | string;
  market_pulse_sector_score?: number | string;
  market_pulse_combined_score?: number | string;
  market_pulse_confidence?: number | string;
  market_pulse_alignment?: string;
  market_pulse_alignment_score?: number | string;
  market_pulse_score_delta?: number | string;
  market_pulse_threshold_add?: number | string;
  market_pulse_size_multiplier?: number | string;
  market_pulse_hard_veto?: boolean | string;
  market_pulse_reason?: string;
  vpin_shock_enabled?: boolean | string;
  vpin_shock_available?: boolean | string;
  vpin_shock_applied?: boolean | string;
  vpin_shock_shadow_mode?: boolean | string;
  vpin_shock_scope?: string;
  vpin_shock_symbol?: string;
  vpin_shock_sector?: string;
  vpin_shock_alert_level?: string;
  vpin_shock_severity?: string;
  vpin_shock_state?: string;
  vpin_shock_crash_probability?: number | string;
  vpin_shock_breadth_state?: string;
  vpin_shock_breadth_score?: number | string;
  vpin_shock_sector_hit_count?: number | string;
  vpin_shock_market_hit_count?: number | string;
  vpin_shock_age_secs?: number | string;
  vpin_shock_alignment?: string;
  vpin_shock_alignment_score?: number | string;
  vpin_shock_score_delta?: number | string;
  vpin_shock_threshold_add?: number | string;
  vpin_shock_size_multiplier?: number | string;
  vpin_shock_hard_veto?: boolean | string;
  vpin_shock_reason?: string;

  reject_reason: string | null;       // null when recommended=true
  fused_at: string;                   // ISO datetime

  // Attribution / traceability fields
  source_service: string;             // "signal-fusion:proactive-scanner-v2" | "signal-fusion:reactive-consumer-v1"
  scan_run_id: string;                // UUID shared by all symbols in one proactive scan cycle (empty for reactive)
  scan_confluence: string;            // e.g. "2/3" vote tally from proactive scanner (empty for reactive)
  scan_sentiment_at_trigger: string;  // sentiment score at emit time (empty for reactive)
}

export interface FusionSignalsResponse {
  items: FusionSignal[];
  total: number;
  view?: 'latest' | 'history' | 'combined';
  source?: 'all' | 'reactive' | 'proactive';
  storage?: 'redis' | 'database' | 'all';
  since?: string;
  until?: string;
  errors?: Record<string, string>;
  error?: string;
}

// =============================================================================
// Agent Activity types
// =============================================================================

export interface ThresholdBreach {
  metric: string;
  current_value: number;
  threshold: number;
  severity: 'warning' | 'critical';
  direction?: string;
}

export interface ParamUpdate {
  parameter: string;
  old_value: number | string;
  new_value: number | string;
  reason?: string;
}

export interface AgentDecision {
  decision_id: string;
  stream_id: string;
  timestamp: number;
  summary: string;
  breaches: ThresholdBreach[];
  param_updates: ParamUpdate[];
  cline_triggered: boolean;
  cline_prompt_file?: string;
}

export interface AgentDecisionsResponse {
  items: AgentDecision[];
  total: number;
  stream: string;
  error?: string;
}

export interface AgentStatus {
  stream: string;
  total_decisions: number;
  total_breaches: number;
  total_cline_spawns: number;
  total_param_updates: number;
  last_decision_at: number | null;
  error?: string;
}

// =============================================================================
// Model Registry Flywheel types
// =============================================================================

export interface ModelLearningImprovement {
  schema_version?: string;
  validation_kind?: string;
  state?: string;
  improved?: boolean;
  passed?: boolean;
  quality_delta?: number | null;
  score_delta?: number | null;
  profit_factor_delta?: number | null;
  win_rate_delta?: number | null;
  drawdown_delta?: number | null;
  total_return_delta?: number | null;
  positive_metric_deltas?: Record<string, number>;
  risk_reduction_deltas?: Record<string, number>;
  counterfactual_effect_count?: number;
  counterfactual_reject_count?: number;
  counterfactual_size_scaled_count?: number;
  counterfactual_exit_improved_count?: number;
  improved_validation_kinds?: string[];
  held_validation_kinds?: string[];
  missing_validation_kinds?: string[];
  failed_validation_kinds?: string[];
  blockers?: string[];
}

export interface ModelValidationResultState {
  validation_kind: string;
  result_key: string;
  found: boolean;
  status: string;
  result_id: string;
  validation_mode: string;
  blockers: string[];
  read_blockers?: string[];
  metrics: Record<string, unknown>;
  evidence: Record<string, unknown>;
  learning_improvement: ModelLearningImprovement;
  created_at: string;
}

export interface ModelRegistryLayerState {
  target_layer: string;
  active_key: string;
  active: boolean;
  active_registry: Record<string, string>;
  registry_event: Record<string, string>;
  rollback_pointer_key: string;
  rollback_pointer: Record<string, string>;
  rollback_event: Record<string, string>;
  validation_results?: Record<string, ModelValidationResultState>;
  learning_improvement?: ModelLearningImprovement;
  can_request_rollback: boolean;
  blockers: string[];
  read_error?: string | null;
}

export interface ModelRegistryStatusResponse {
  schema_version: string;
  snapshot_ts: string;
  active_key_prefix: string;
  registry_events_stream: string;
  rollback_requests_stream: string;
  count: number;
  active_count: number;
  rollback_ready_count: number;
  learning_improved_count?: number;
  learning_missing_count?: number;
  learning_failed_count?: number;
  layers: ModelRegistryLayerState[];
}

export interface ModelRollbackRequestEntry {
  stream_id?: string;
  schema_version?: string;
  rollback_request_id?: string;
  target_layer?: string;
  active_registry_event_id?: string;
  rollback_target_id?: string;
  approval_source?: string;
  requires_human_approval?: boolean;
  attribution_id?: string;
  recommendation_id?: string;
  position_id?: string;
  signal_id?: string;
  symbol?: string;
  direction?: string;
  signal_type?: string;
  reason_codes?: string[];
  created_at?: string;
  requested_by?: string;
  request_source?: string;
  reason?: string;
}

export interface ModelRollbackRequestsResponse {
  schema_version: string;
  snapshot_ts: string;
  stream_key: string;
  count: number;
  requests: ModelRollbackRequestEntry[];
}

export interface PublishModelRollbackRequestPayload {
  target_layer: string;
  reason: string;
  active_registry_event_id?: string;
  rollback_target_id?: string;
  reason_codes?: string[];
  requested_by?: string;
  dry_run?: boolean;
}

export interface PublishModelRollbackRequestResponse {
  schema_version: string;
  accepted: boolean;
  dry_run: boolean;
  stream_key: string;
  stream_id: string;
  active_key: string;
  rollback_pointer_key: string;
  request: ModelRollbackRequestEntry;
}

// =============================================================================
// Fast Policy Flywheel types
// =============================================================================

export interface FastPolicyKeyFamily {
  target_layer: string;
  family: string;
  pattern: string;
}

export interface FastPolicyRow {
  key: string;
  family: string;
  target_layer: string;
  target_service: string;
  target_key: string;
  schema_version: string;
  source: string;
  decision: string;
  runtime_value: Record<string, unknown>;
  bounds: Record<string, unknown>;
  reason_codes: string[];
  sample_count: number;
  policy_version: string;
  attribution_id: string;
  policy_tuning_job_id: string;
  target_policy_id: string;
  aggregate_id: string;
  aggregate_key: string;
  symbol: string;
  direction: string;
  signal_type: string;
  updated_at: string;
  expires_at: string;
  ttl_seconds: number | null;
  ttl_state: string;
  active: boolean;
  blockers: string[];
  fields: Record<string, string>;
}

export interface FastPolicyStatusResponse {
  schema_version: string;
  snapshot_ts: string;
  redis_database: string;
  limit_per_family: number;
  key_families: FastPolicyKeyFamily[];
  count: number;
  active_count: number;
  blocker_count: number;
  family_counts: Record<string, number>;
  layer_counts: Record<string, number>;
  source_counts: Record<string, number>;
  rows: FastPolicyRow[];
  errors: Array<{ pattern?: string; key?: string; error: string }>;
}

// =============================================================================
// Learning Progress types
// =============================================================================

export interface LearningProgressConsumerGroup {
  name: string;
  consumers: number;
  active: boolean;
  pending: number;
  lag: number | null;
  last_delivered_id: string;
}

export interface LearningProgressStream {
  stream_key: string;
  stage: string;
  label: string;
  owner: string;
  receiver: string;
  exists: boolean;
  status: string;
  length: number;
  last_entry_id: string;
  last_entry_at: string;
  last_entry_age_seconds: number | null;
  consumer_group_count: number;
  active_consumer_group_count: number;
  inactive_consumer_group_count: number;
  pending_messages: number;
  lag: number | null;
  active_pending_messages: number;
  active_lag: number | null;
  inactive_consumer_groups: string[];
  consumer_groups: LearningProgressConsumerGroup[];
  read_error?: string;
}

export interface LearningProgressRecentSummary {
  limit: number;
  outcome_count: number;
  training_row_count: number;
  policy_job_count: number;
  validation_event_count: number;
  outcome_bucket_counts: Record<string, number>;
  outcome_scope_counts: Record<string, number>;
  training_layer_counts: Record<string, number>;
  training_label_counts: Record<string, number>;
  training_scope_counts: Record<string, number>;
  training_source_counts?: Record<string, number>;
  training_category_counts?: Record<string, number>;
  pak_training_row_count?: number;
  pak_training_eligible_count?: number;
  pak_training_eligible_rate?: number | null;
  pak_training_layer_counts?: Record<string, number>;
  pak_training_scope_counts?: Record<string, number>;
  pak_probability_source_counts?: Record<string, number>;
  pak_direction_hit_counts?: Record<string, number>;
  pak_direction_hit_rate?: number | null;
  pak_realized_pnl_sum?: number;
  pak_realized_pnl_count?: number;
  pak_avg_abs_calibration_error?: number | null;
  pak_recent_rows?: Array<{
    stream_id: string;
    ts: string;
    training_row_id: string;
    target_layer: string;
    feedback_learning_scope: string;
    source: string;
    attribution_category: string;
    probability_source: string;
    direction_hit: boolean | null;
    realized_pnl: number | null;
    calibration_error: number | null;
  }>;
  policy_layer_counts: Record<string, number>;
  policy_decision_counts: Record<string, number>;
  validation_status_counts: Record<string, number>;
  validation_kind_counts: Record<string, number>;
}

export interface LearningProgressBoundary {
  mode: string;
  pta_status: string;
  feedback_state: string;
  live_execution_enabled: boolean;
  live_authorization_ready: boolean;
  learning_gaps: number;
  pending_promotion_decisions: number;
  stale_pending_promotion_decisions: number;
  closed_loop_complete: number;
  promotion_reviews_observed: number;
  promotion_holds_observed: number;
  cutover_status: string;
  cutover_hold_count: number;
  requires_explicit_cutover: boolean;
  next_action: string;
  blockers: string[];
}

export interface LearningProgressRouting {
  streams: LearningProgressStream[];
  stream_count: number;
  acknowledged_streams: number;
  active_acknowledged_streams: number;
  pending_messages: number;
  lag: number;
  active_pending_messages: number;
  active_lag: number;
  inactive_consumer_group_count: number;
  backlog_streams: string[];
  active_backlog_streams: string[];
  recent: LearningProgressRecentSummary;
  lifecycle: Array<{ name: string; owner: string; output: string }>;
}

export interface LearningProgressLayerMeasurement {
  target_layer: string;
  active: boolean;
  candidate_id: string;
  active_model_id: string;
  active_policy_id: string;
  learning_state: string;
  improved: boolean;
  score_delta: number;
  profit_factor_delta: number;
  win_rate_delta: number;
  drawdown_delta: number;
  total_return_delta: number;
  quality_delta: number | null;
  counterfactual_effect_count: number;
  blockers: string[];
}

export interface LearningProgressGap {
  severity: 'danger' | 'warning' | 'info' | string;
  title: string;
  detail: string;
}

export interface LearningProgressResponse {
  schema_version: string;
  snapshot_ts: string;
  health: 'healthy' | 'watch' | 'critical' | string;
  boundary: LearningProgressBoundary;
  routing: LearningProgressRouting;
  actions: {
    fast_policy: {
      count: number;
      active_count: number;
      blocker_count: number;
      family_counts: Record<string, number>;
      layer_counts: Record<string, number>;
      source_counts: Record<string, number>;
      rows: FastPolicyRow[];
    };
    pta_promotions: {
      summary: PtaPromotionGapTargetsResponse['summary'];
      rows: PtaPromotionGapTargetRow[];
      status: string;
    };
    model_registry: {
      count: number;
      active_count: number;
      rollback_ready_count: number;
      learning_improved_count: number;
      learning_missing_count: number;
      learning_failed_count: number;
    };
  };
  measurement: {
    model_registry: {
      count: number;
      active_count: number;
      learning_improved_count: number;
      learning_missing_count: number;
      learning_failed_count: number;
    };
    layers: LearningProgressLayerMeasurement[];
  };
  gaps: LearningProgressGap[];
  sources: Record<string, unknown>;
}

// =============================================================================
// Service Cleanup Readiness types
// =============================================================================

export interface ServiceCleanupReadinessSummary {
  cleanup_candidates: number;
  cleanup_allowed: number;
  cleanup_blocked: number;
  by_decision: Record<string, number>;
  by_workspace: Record<string, number>;
  by_source?: Record<string, number>;
  tracked_sibling_services?: number;
  sibling_cleanup_candidates?: number;
  dependent_cutovers?: ServiceCleanupDependentCutoverSummary;
  pta_transitions?: ServiceCleanupTransitionSummary;
  split_transitions?: ServiceCleanupTransitionSummary;
  pta_entry_evidence?: ServiceCleanupEntryEvidence;
  pta_cutover_plan?: ServiceCleanupPtaCutoverPlan;
}

export interface ServiceCleanupEntryEvidence {
  state?: string;
  next_action?: string;
  owner_service?: string;
  required_evidence?: string;
  primary_blocker?: string;
  latest_paper_canary_block_reason?: string;
  latest_paper_canary_throttle_block?: string;
  latest_paper_canary_hard_reject_marker?: string;
  latest_paper_canary_hard_reject_reason?: string;
  latest_paper_canary_retry_after_seconds?: number | null;
  latest_paper_canary_min_interval_seconds?: number | null;
  verified_packets?: number | null;
  accepted_packets_requiring_action?: number | null;
  historical_gate_stage_samples_ready?: boolean;
  recent_gate_stage_samples_ready?: boolean;
}

export interface ServiceCleanupDependentCutoverSummary {
  total: number;
  ready: number;
  pending_required: number;
  pending_optional: number;
  by_candidate: Record<
    string,
    {
      total: number;
      ready: number;
      pending_required: number;
      pending_optional: number;
    }
  >;
}

export interface ServiceCleanupPtaCutoverPlanItem {
  blocker: string;
  title: string;
  category: string;
  status: string;
  required_action: string;
  approval_required: boolean;
  required_env: Record<string, unknown>;
  required_policy: Record<string, unknown>;
  evidence_required: string[];
  affected_gates: string[];
  recommended_env: Record<string, unknown>;
  notes: string[];
}

export interface ServiceCleanupPtaCutoverPlan {
  status: string;
  mode: string;
  requires_explicit_cutover: boolean;
  hold_count: number;
  categories: Record<string, number>;
  items: ServiceCleanupPtaCutoverPlanItem[];
  source?: string;
  source_url?: string;
  error?: string;
}

export interface ServiceCleanupTransitionCandidateSummary {
  owner_ready: boolean;
  runtime_ready: boolean;
  observation_ready?: boolean;
  live_cutover_ready?: boolean;
  cutover_ready: boolean;
  removal_ready: boolean;
  decision: string;
  blockers: string[];
}

export interface ServiceCleanupTransitionSummary {
  total: number;
  owner_ready: number;
  runtime_ready: number;
  observation_ready?: number;
  live_cutover_ready?: number;
  cutover_ready: number;
  removal_ready: number;
  cutover_pending: number;
  removal_pending: number;
  runtime_ready_cutover_pending: number;
  cutover_ready_removal_pending?: number;
  by_candidate: Record<string, ServiceCleanupTransitionCandidateSummary>;
}

export interface ServiceCleanupDependentCutover {
  dependent: string;
  current_target: string;
  replacement_target: string;
  status: string;
  required_before_removal: boolean;
  blocker: string;
  evidence_ref: string;
}

export interface ServiceCleanupCandidate {
  service: string;
  source?: string;
  target_plane: string;
  target_workspace: string;
  migration_state: string;
  cleanup_gate: string;
  cleanup_allowed: boolean;
  decision: string;
  blockers: string[];
  evidence: Record<string, unknown> & {
    dependent_cutovers?: ServiceCleanupDependentCutover[];
    dependent_cutovers_ready?: boolean;
    dependent_cutover_blockers?: string[];
    runtime_checks?: Record<string, unknown>;
  };
}

export interface ServiceCleanupTrackedSibling extends ServiceCleanupCandidate {
  path: string;
  cleanup_candidate: boolean;
}

export interface ServiceCleanupReadinessResponse {
  schema_version: string;
  manifest_schema_version: string;
  pta_boundary_health_status: string;
  pta_boundary_health_source?: string;
  pta_boundary_health_source_url?: string;
  summary: ServiceCleanupReadinessSummary;
  candidates: ServiceCleanupCandidate[];
  tracked_sibling_services?: ServiceCleanupTrackedSibling[];
  sources?: {
    manifest_path?: string;
    pta_evidence_path?: string;
    platform_evidence_path?: string;
    split_evidence_path?: string;
    pta_boundary_health_url?: string;
    pta_cutover_preflight_url?: string;
  };
}

export interface PtaCapitalBoundaryHealthSection {
  state?: string;
  ready?: boolean;
  blockers?: string[];
  samples?: Record<string, unknown>;
}

export interface PtaCapitalBoundaryHealthResponse {
  schema_version?: string;
  status: string;
  next_action?: string;
  operator_message?: string;
  blockers?: string[];
  summary?: {
    live_execution_enabled?: boolean;
    entry_shadow_ready?: boolean;
    exit_shadow_ready?: boolean;
    exit_authority_ready?: boolean;
    bridge_observation_ready?: boolean;
    bridge_forwarding_ready?: boolean;
    live_authorization_ready?: boolean;
    feedback_state?: string;
    learning_gaps?: number;
    closed_loop_complete?: number;
    promotion_reviews_observed?: number;
    promotion_holds_observed?: number;
    pending_promotion_decisions?: number;
    stale_pending_promotion_decisions?: number;
    [key: string]: unknown;
  };
  sections?: Record<string, PtaCapitalBoundaryHealthSection>;
  source?: string;
  source_url?: string;
  error?: string;
}

export interface PtaCutoverPlanItem {
  title?: string;
  category?: string;
  status?: string;
  blocker?: string;
  required_action?: string;
  approval_required?: boolean;
  affected_gates?: string[];
  evidence_required?: string[];
  notes?: string[];
  required_env?: Record<string, string>;
  recommended_env?: Record<string, string>;
  required_policy?: Record<string, unknown>;
}

export interface PtaCutoverPlan {
  status?: string;
  mode?: string;
  requires_explicit_cutover?: boolean;
  hold_count?: number;
  categories?: Record<string, number>;
  items?: PtaCutoverPlanItem[];
}

export interface PtaCutoverPreflightResponse {
  schema_version?: string;
  status?: string;
  next_action?: string;
  blockers?: string[];
  cutover_plan?: PtaCutoverPlan;
  entry_diagnostics?: Record<string, unknown>;
  gates?: Record<string, unknown>;
  source?: string;
  source_url?: string;
  error?: string;
}

export interface PtaPromotionGapTargetRow {
  closed_loop_state?: string;
  feedback_training_id?: string;
  attribution_id?: string;
  observed_at?: string;
  age_seconds?: number | null;
  stale?: boolean;
  aliases?: string[];
  target_aliases?: string[];
  training_row_ids?: string[];
  training_rows?: Record<string, unknown>[];
  target_layers?: string[];
  target_models?: string[];
  target_policies?: string[];
  learning_scopes?: string[];
  primary_bucket?: string;
  reason_codes?: string[];
  [key: string]: unknown;
}

export interface PtaPromotionGapTargetsResponse {
  schema_version?: string;
  status?: string;
  summary?: {
    with_feedback?: number;
    pending_promotion_decisions?: number;
    stale_pending_promotion_decisions?: number;
    pending_promotion_stale_seconds?: number;
    oldest_pending_promotion_observed_at?: string;
    latest_pending_promotion_observed_at?: string;
    oldest_pending_promotion_age_seconds?: number | null;
    rows_returned?: number;
    truncated?: boolean;
    [key: string]: unknown;
  };
  rows?: PtaPromotionGapTargetRow[];
  source?: string;
  source_url?: string;
  error?: string;
}

export interface AgentReviewProposal {
  proposal_type: string;
  target_service: string;
  payload: Record<string, unknown>;
}

export interface AgentReviewItem {
  review_item_id: string;
  stream_id?: string;
  created_at: string;
  status: string;
  diagnosis_type: string;
  cohort_scope_type: string;
  cohort_scope_key: string;
  source_service: string;
  signal_type?: string;
  symbol?: string;
  direction?: string;
  regime?: string;
  session?: string;
  sample_count?: number;
  live_sample_count?: number;
  label_coverage?: number;
  entry_quality_mean?: number;
  exit_quality_mean?: number;
  expectancy_bps?: number;
  hard_stop_rate?: number;
  no_mfe_rate?: number;
  mfe_capture_mean?: number;
  post_exit_validation_rate?: number;
  missed_followthrough_bps_mean?: number;
  premature_exit_rate?: number;
  horizon_mismatch_rate?: number;
  confidence_band?: string;
  proposal?: AgentReviewProposal;
  proposal_type?: string;
  proposal_payload?: Record<string, unknown>;
  risk_summary?: Record<string, unknown>;
  readiness_blockers?: string[];
  audit_hash?: string;
  reviewed_at?: string;
  reviewed_by?: string;
  operator_action?: string;
  operator_reason?: string;
}

export interface AgentReviewItemsResponse {
  items: AgentReviewItem[];
  total: number;
  stream: string;
  error?: string;
}

export interface AgentReviewItemActionResponse {
  item: AgentReviewItem;
}

// =============================================================================
// Metrics-API types (paper trading state — sourced from port 18003)
// =============================================================================

export interface MetricsSignalData {
  signal_type: string;
  sample_size: number;
  win_rate: number;
  profit_factor: number;
  avg_realized_edge_bps: number;
  sharpe_of_returns: number | null;
  edge_calibration: number;
  is_valid: boolean;
}

export interface MetricsPortfolioData {
  total_pnl_bps: number;
  total_trades: number;
  balance_usd: number;
  equity_curve: Array<{ ts: string; value: number }>;
  last_updated: string;
  trading_tier: 'paper' | 'live';
  /** Daily P&L in USD (execution service computed) */
  daily_pnl?: number;
  /** Active profile name */
  profile?: string;
  /** Data age in milliseconds (how stale the underlying data is) */
  data_age_ms?: number;
}

export interface MetricsPerformanceData {
  sharpe_ratio: number | null;
  profit_factor: number;
  win_rate: number;
  avg_win_bps: number;
  avg_loss_bps: number;
  total_signals: number;
  last_updated: string;
  data_age_ms?: number;
}

export interface MetricsRiskData {
  rolling_drawdown: number;
  max_drawdown: number;
  drawdown_breaker_tripped: boolean;
  last_updated: string;
  /** Current safety state */
  current_profile?: string;
  safety_state?: string;
  data_age_ms?: number;
}

export interface MetricsRegimeData {
  current_regime: string;
  confidence: number;
  regime_source: string;
  consecutive_bars: number;
  last_transition_at: string | null;
  last_updated: string;
  data_age_ms?: number;
  /** True when regime detector has enough history (>= 30 samples) for reliable thresholds */
  is_warmed_up?: boolean;
  /** Current RV history samples collected */
  warmup_rv_samples?: number;
  /** Current VoV history samples collected */
  warmup_vov_samples?: number;
  /** Required samples for warmup (typically 30) */
  warmup_required_samples?: number;
}

export interface MetricsSummaryData {
  portfolio: MetricsPortfolioData;
  performance: MetricsPerformanceData;
  risk: MetricsRiskData;
  signals: Record<string, MetricsSignalData>;
  snapshot_ts: string;
  system_healthy: boolean;
  trading_tier: 'paper' | 'live';
  /** Active profile name */
  current_profile?: string;
  /** System safety state */
  safety_state?: string;
  /** Current market regime */
  regime?: MetricsRegimeData;
  /** Staleness of underlying data in milliseconds */
  data_age_ms?: number;
}

// Feasibility Status types (from /feasibility/status endpoint)
export interface FeasibilityStatusData {
  service: string;
  running: boolean;
  fail_open: {
    active: boolean;
    reason: string | null;
    last_modified_signal_time: string;
    threshold_seconds: number;
  };
  degraded_mode: {
    mode: string;
    reason: string | null;
    redis_disconnect_time: string | null;
    qdrant_disconnect_time: string | null;
    quarantine_all_intents: boolean;
  };
  verdict_distribution: {
    approved: number;
    rejected: number;
    sized_down: number;
  };
  hero_signals: string[];
  confluence_window: {
    tracked_keys: number;
    window_seconds: number;
  };
  venue_health: Record<string, {
    state: string;
    timestamp: string | null;
  }>;
  opposing_signals: {
    active_entries: number;
    decay_hours: number;
  };
  policy_hash_valid: boolean;
  active_profile_id: string | null;
  policy_name: string;
}

// =============================================================================
// Position Monitor Status types (ADR-036 LiveSignalMonitor + ADR-038 TrailingStopOptimizer)
// =============================================================================

/**
 * Per-position composite health score from LiveSignalMonitor (ADR-036).
 * Weighted exit-pressure factors are combined into a single live_signal_score.
 */
export interface PositionLiveScore {
  symbol: string;
  /** Composite score ∈ [0,1]. ≤0.32 → force exit; ≥0.85 → tighten/partial exit */
  last_score: number;
  /** Weighted component sub-scores. Some raw diagnostics can be outside [0,1]. */
  components: {
    /** quality_score × calibrated_confidence (weight 0.16) */
    quality_score: number;
    /** signal decay factor (weight 0.16) */
    decay_factor: number;
    /** HMM regime persistence scalar (weight 0.12) */
    regime_persistence: number;
    /** price performance vs entry (weight 0.15) */
    price_performance: number;
    /** VPIN health — inverted z-score (weight 0.10) */
    vpin_health: number;
    /** orderbook bid/ask imbalance (weight 0.08) */
    orderbook_imbalance: number;
    /** trailing drawdown from peak — inverted (weight 0.05) */
    trailing_drawdown: number;
    /** ATR stop proximity (weight 0.05). Negative means through the ATR trail. */
    atr_proximity?: number;
    /** GP price-forecast directional alignment (weight 0.04) */
    gp_forecast_alignment?: number;
    /** Macro/regime scalar diagnostic */
    macro_regime_scalar?: number;
    /** Raw BOCD changepoint probability; displayed as stability in the UI (weight 0.03) */
    bocd_p_changepoint?: number;
    /** Hawkes OFI diagnostic blended into orderbook health */
    hawkes_ofi?: number;
    /** Directional-bias alignment (weight 0.06) */
    db_alignment?: number;
  };
  last_check_at: string | null;
  /** Most recent threshold-triggered action */
  action_taken: 'none' | 'force_exit' | 'tighten_stop' | 'partial_exit' | 'tighten_and_partial' | 'exit_suppressed';
  /** Whether the one-shot 30% partial exit has already fired this position */
  partial_exit_done: boolean;
  is_active: boolean;
  /** ADR-036: consecutive sub-threshold ticks before exit fires (flash-crash guard) */
  consecutive_bad_checks?: number;
  /** Position side ("long" | "short") */
  side?: string;
  /** Entry price for this position */
  entry_price?: number;
  /** Peak price since position opened */
  peak_price?: number;
  /** Total monitor check cycles run */
  checks?: number;
  /** ISO timestamp when monitor started */
  started_at?: string | null;
  /** True once trailing stop has been tightened this session */
  stop_tightened?: boolean;
  /** Rate of score change per check cycle (positive = improving, negative = deteriorating) */
  score_velocity?: number;
  /** Expected hold duration in hours derived from the fusion signal's hold estimate */
  hold_estimate_hours?: number;
  /** Human-readable hold tier: "scalp" | "intraday" | "swing" | "multiday" */
  hold_estimate_label?: string;
  /** Half-life of the hold estimate signal in seconds */
  hold_estimate_half_life_s?: number;
  /** Position age ÷ hold estimate; >1.0 means position is overdue for exit review */
  overdue_factor?: number;
  /** Param audit verdict: 'ok' | 'stale' | 'extreme' | 'n/a' */
  param_audit_verdict?: string;
}

/**
 * Per-position trailing stop + TP optimizer status (ADR-038).
 * Active means the runner optimizer task is supervising stop/TP rails.
 */
export interface PositionOptimizerStatus {
  symbol: string;
  /** true when the optimizer task is supervising this position */
  is_active: boolean;
  current_stop: number | null;
  current_tp: number | null;
  /** Number of stop ratchet adjustments this position session */
  stop_updates: number;
  /** Number of TP raise adjustments this position session */
  tp_updates: number;
  last_stop_update_at: string | null;
  last_tp_update_at: string | null;
  last_score?: number | null;
  last_price?: number | null;
  last_check_at?: string | null;
  started_at?: string | null;
}

/**
 * Combined per-position monitor status (ADR-036 + ADR-037 + ADR-038).
 */
export interface PositionMonitorStatus {
  symbol: string;
  position_id?: string | null;
  entry_intent_id?: string | null;
  side?: string | null;
  size?: number | null;
  entry_price?: number | null;
  current_price?: number | null;
  market_value?: number | null;
  unrealized_pnl?: number | null;
  unrealized_pnl_pct?: number | null;
  holding_period_hours?: number | null;
  signal_type?: string | null;
  entry_signal_type?: string | null;
  /** LiveSignalMonitor status; null if monitor not started for this symbol */
  live_signal: PositionLiveScore | null;
  /** TrailingStopOptimizer status; null if not started */
  optimizer: PositionOptimizerStatus | null;
  /** Whether the RegimePositionMonitor (ADR-037) is running for this position */
  regime_monitor_active: boolean;
}

export interface PositionMonitorResponse {
  positions: PositionMonitorStatus[];
  monitored_count: number;
  generated_at: string;
  error?: string;
}

// Filtered Signal types
export interface FilteredSignal {
  id: string;
  signal_id: string;
  signal_type: string;
  symbol: string;
  direction: string;
  confidence: number;
  reason: string;
  details: Record<string, unknown>;
  timestamp: string;
}

export interface FilteredSignalsResponse {
  items: FilteredSignal[];
  total: number;
  filters: {
    reason: string | null;
    signal_type: string | null;
    symbol: string | null;
  };
  error?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get TradeOS system status
 */
export const getStatus = () => 
  request<TradeOSStatus>('/tool/status');

/**
 * Get pending intents awaiting approval
 */
export const getPendingIntents = () => 
  request<{ items: Intent[] }>('/tool/intents/pending');

/**
 * Propose a new trading intent
 */
export const proposeIntent = (req: ProposeIntentRequest) =>
  request<Intent>('/tool/intent/propose', {
    method: 'POST',
    body: JSON.stringify(req),
  });

/**
 * Approve a pending intent
 */
export const approveIntent = (intentId: string) =>
  request<{ ok: boolean; intent: Intent; order?: Order }>(`/tool/intent/approve/${intentId}`, {
    method: 'POST',
  });

/**
 * Reject a pending intent
 */
export const rejectIntent = (intentId: string) =>
  request<{ ok: boolean; intent: Intent }>(`/tool/intent/reject/${intentId}`, {
    method: 'POST',
  });

/**
 * Enable kill switch (stops all trading)
 */
export const enableKillSwitch = () =>
  request<{ ok: boolean; kill_switch: string; message?: string }>('/tool/kill_switch/on', {
    method: 'POST',
  });

/**
 * Disable kill switch (resumes trading)
 */
export const disableKillSwitch = () =>
  request<{ ok: boolean; kill_switch: string; message?: string }>('/tool/kill_switch/off', {
    method: 'POST',
  });

/**
 * Get open positions
 */
export const getPositions = () =>
  request<{ items: Position[] }>('/tool/positions');

export const getTradeMovie = (
  entityType: TradeMovieEntityType,
  entityId: string,
  signal?: AbortSignal,
) =>
  request<TradeMovie>(
    `/api/trade-movies/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    { signal },
  );

export const runCounterfactual = (payload: CounterfactualRunRequest) =>
  request<CounterfactualSubmitResponse>('/api/counterfactuals/run', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getCounterfactualResult = (jobId: string, signal?: AbortSignal) =>
  request<CounterfactualResult>(`/api/counterfactuals/${encodeURIComponent(jobId)}`, { signal });

export const getCounterfactualEvents = (jobId: string, signal?: AbortSignal) =>
  request<CounterfactualEventsResponse>(
    `/api/counterfactuals/${encodeURIComponent(jobId)}/events`,
    { signal },
  );

/**
 * Get recently closed positions from the positions.close stream
 */
export const getClosedPositions = (limit: number = 50) => {
  const params = new URLSearchParams({ limit: String(limit) });
  return request<{ items: Position[] }>(`/tool/positions/closed?${params.toString()}`);
};

/**
 * Get dashboard portfolio data
 */
export const getDashboardPortfolio = () =>
  request<DashboardPortfolio>('/api/dashboard/portfolio');

/**
 * Get portfolio history for chart
 */
export const getPortfolioHistory = (
  timeframe: '24h' | '7d' | '30d' | '90d' | 'ytd' = '30d',
  signal?: AbortSignal,
) => {
  const params = new URLSearchParams({ timeframe });
  return request<PortfolioHistory>(`/api/dashboard/portfolio/history?${params.toString()}`, { signal });
};

/**
 * Get execution statistics
 */
export const getStats = () =>
  request<Record<string, unknown>>('/tool/stats');

/**
 * Get pipeline health status
 */
export const getPipelineHealth = () =>
  request<PipelineHealth>('/tool/pipeline');

/**
 * Get detailed health of all services
 */
export const getDetailedHealth = () =>
  request<DetailedHealth>('/tool/health/detailed');

/**
 * Get trading opportunities from scanner
 */
export const getScannerOpportunities = (limit: number = 10, symbol?: string) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (symbol) params.append('symbol', symbol);
  return request<{ items: ScannerOpportunity[]; total: number; error?: string }>(
    `/tool/scanner/opportunities?${params.toString()}`
  );
};

/**
 * Chat with the assistant
 */
export const assistantChat = (req: AssistantChatRequest) =>
  request<AssistantChatResponse>('/assistant/chat', {
    method: 'POST',
    body: JSON.stringify(req),
    timeoutMs: ASSISTANT_API_TIMEOUT,
  });

const parseAssistantStreamFrame = (
  rawFrame: string,
): { event: string; data: unknown } | null => {
  const normalized = rawFrame.replace(/\r\n/g, '\n').trim();
  if (!normalized) return null;

  let event = 'message';
  const dataLines: string[] = [];
  normalized.split('\n').forEach((line) => {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  });

  if (dataLines.length === 0) return null;
  const dataText = dataLines.join('\n');
  try {
    return { event, data: JSON.parse(dataText) };
  } catch {
    return { event, data: dataText };
  }
};

const assistantStreamErrorMessage = (event: AssistantChatStreamError): string => {
  if (typeof event.error === 'string') return event.error;
  return event.error?.message || event.error?.detail || 'Assistant stream failed';
};

/**
 * Stream assistant lifecycle events over the POST-based SSE endpoint.
 */
export async function assistantChatStream(
  req: AssistantChatRequest,
  handlers: AssistantChatStreamHandlers = {},
  options: { signal?: AbortSignal } = {},
): Promise<AssistantChatResponse> {
  const url = `${API_BASE_URL}/assistant/chat/stream`;
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = getAuthToken();
  if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), ASSISTANT_STREAM_TIMEOUT);
  const mergedSignal = options.signal
    ? AbortSignal.any([options.signal, timeoutController.signal])
    : timeoutController.signal;

  let finalResponse: AssistantChatResponse | null = null;

  const dispatchFrame = (rawFrame: string): boolean => {
    const parsed = parseAssistantStreamFrame(rawFrame);
    if (!parsed) return false;

    if (parsed.event === 'ack') {
      handlers.onAck?.(parsed.data as AssistantChatStreamAck);
    } else if (parsed.event === 'progress') {
      handlers.onProgress?.(parsed.data as AssistantChatStreamProgress);
    } else if (parsed.event === 'sources') {
      handlers.onSources?.(parsed.data as AssistantChatStreamSources);
    } else if (parsed.event === 'delta') {
      handlers.onDelta?.(parsed.data as AssistantChatStreamDelta);
    } else if (parsed.event === 'final') {
      finalResponse = parsed.data as AssistantChatResponse;
      handlers.onFinal?.(parsed.data as AssistantChatResponse & { request_id?: string });
      return true;
    } else if (parsed.event === 'error') {
      const streamError = parsed.data as AssistantChatStreamError;
      handlers.onError?.(streamError);
      throw new TradeOSError(
        streamError.status_code || 0,
        assistantStreamErrorMessage(streamError),
        streamError,
      );
    }

    return false;
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: defaultHeaders,
      body: JSON.stringify(req),
      signal: mergedSignal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = extractErrorMessage(errorData, `HTTP error! status: ${response.status}`);
      throw new TradeOSError(response.status, errorMessage, errorData);
    }

    if (!response.body) {
      throw new TradeOSError(0, 'Assistant stream returned an empty response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let frameEnd = buffer.indexOf('\n\n');
      while (frameEnd >= 0) {
        const frame = buffer.slice(0, frameEnd);
        buffer = buffer.slice(frameEnd + 2);
        if (dispatchFrame(frame) && finalResponse) {
          await reader.cancel().catch(() => undefined);
          return finalResponse;
        }
        frameEnd = buffer.indexOf('\n\n');
      }
    }

    buffer += decoder.decode();
    if (buffer.trim()) {
      if (dispatchFrame(buffer) && finalResponse) {
        await reader.cancel().catch(() => undefined);
        return finalResponse;
      }
    }

    if (!finalResponse) {
      throw new TradeOSError(0, 'Assistant stream ended before the final response arrived');
    }
    return finalResponse;
  } catch (error) {
    if (error instanceof TradeOSError) {
      throw error;
    }
    if (error instanceof Error && error.name === 'AbortError') {
      if (options.signal?.aborted) throw error;
      throw new TradeOSError(408, 'Request timeout');
    }
    throw new TradeOSError(0, error instanceof Error ? error.message : 'Unknown error');
  } finally {
    clearTimeout(timeoutId);
  }
}

export const getAssistantReportJob = (jobId: string) =>
  request<AssistantReportJobResponse>(`/assistant/report-jobs/${encodeURIComponent(jobId)}`, {
    timeoutMs: ASSISTANT_API_TIMEOUT,
  });

/**
 * Execute an assistant command
 */
export const assistantExecute = (command: Record<string, unknown>) =>
  request<AssistantExecuteResponse>('/assistant/execute', {
    method: 'POST',
    body: JSON.stringify({ command }),
  });

/**
 * Get assistant conversation history
 */
export const getAssistantHistory = (chatId?: string, limit: number = 50) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (chatId) params.append('chat_id', chatId);
  return request<AssistantHistoryResponse>(`/assistant/history?${params.toString()}`);
};

/**
 * Clear assistant conversation history
 */
export const clearAssistantHistory = (chatId?: string) => {
  const params = chatId ? new URLSearchParams({ chat_id: chatId }).toString() : '';
  return request<{ ok: boolean; message: string }>(`/assistant/history${params ? `?${params}` : ''}`, {
    method: 'DELETE',
  });
};

/**
 * Get signal accuracy data from TCA Monitor
 */
export const getSignalAccuracy = (period: string = '24h') => {
  const params = new URLSearchParams({ period });
  return request<AccuracyResponse>(`/api/v1/accuracy/all?${params.toString()}`);
};

/**
 * Get ADR-126 EA entry-quality readiness report from TCA Monitor.
 */
export const getEAEntryQualityReadiness = (maxCohorts = 50, scopeType?: string) => {
  const params = new URLSearchParams({ max_cohorts: String(maxCohorts) });
  if (scopeType) params.append('scope_type', scopeType);
  return request<EAEntryQualityReadinessReport>(`/api/v1/ea-entry-quality/readiness?${params.toString()}`);
};

/**
 * Get execution quality metrics from TCA Monitor
 */
export const getExecutionQuality = () =>
  request<ExecutionQualityData>('/api/v1/execution/quality');

/**
 * Get execution latency metrics from TCA Monitor
 */
export const getExecutionLatency = (venue?: string) => {
  const params = venue ? new URLSearchParams({ venue }).toString() : '';
  return request<ExecutionLatencyData>(`/api/v1/execution/latency${params ? `?${params}` : ''}`);
};

/**
 * Get risk exposure data
 */
export const getRiskExposure = () =>
  request<RiskExposure>('/api/v1/risk/exposure');

/**
 * Get drawdown data
 */
export const getRiskDrawdown = () =>
  request<RiskDrawdown>('/api/v1/risk/drawdown');

/**
 * Manually acknowledge (reset) a drawdown warning banner.
 * Sets a server-side Redis ack key with a 1-hour TTL.
 */
export const resetDrawdownBreaker = () =>
  request<{ ok: boolean; message: string }>('/api/v1/risk/drawdown/reset', {
    method: 'POST',
  });

/**
 * Reset the current UTC-day daily-loss baseline so entry gating can resume.
 */
export const resetDailyLossGate = () =>
  request<Record<string, unknown>>('/api/v1/risk/daily-loss/reset', {
    method: 'POST',
  });

/**
 * Get stress test results
 */
export const getRiskStressTest = () =>
  request<RiskStressTest>('/api/v1/risk/stress-test');

/**
 * Get risk events
 */
export const getRiskEvents = (options?: {
  limit?: number;
  cursor?: string;
  severity?: string;
  category?: string;
}) => {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.cursor) params.append('cursor', options.cursor);
  if (options?.severity) params.append('severity', options.severity);
  if (options?.category) params.append('category', options.category);
  const queryString = params.toString();
  return request<RiskEventsResponse>(`/api/v1/risk/events${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get normalized admin breaker registry for the Risk Command Center.
 */
export const getAdminBreakers = (options?: {
  active_only?: boolean;
  limit?: number;
}) => {
  const params = new URLSearchParams();
  if (options?.active_only) params.append('active_only', 'true');
  if (options?.limit) params.append('limit', String(options.limit));
  const queryString = params.toString();
  return request<AdminBreakersResponse>(`/api/v1/admin/breakers${queryString ? `?${queryString}` : ''}`).then((response) => ({
    ...response,
    items: (response.items ?? []).map((item) => ({
      ...item,
      available_actions: Array.isArray(item.available_actions)
        ? item.available_actions
        : Array.isArray(item.allowed_actions)
          ? item.allowed_actions
          : [],
    })),
  }));
};

const postBreakerAction = (
  breakerId: string,
  actionPath: 'reset-counter' | 'clear-cooldown' | 'disable' | 're-enable',
  payload: AdminBreakerActionRequest,
) => request<Record<string, unknown>>(
  `/api/v1/admin/breakers/${encodeURIComponent(breakerId)}/${actionPath}`,
  {
    method: 'POST',
    body: JSON.stringify(payload),
  },
);

export const resetAdminBreakerCounter = (breakerId: string, payload: AdminBreakerActionRequest) =>
  postBreakerAction(breakerId, 'reset-counter', payload);

export const clearAdminBreakerCooldown = (breakerId: string, payload: AdminBreakerActionRequest) =>
  postBreakerAction(breakerId, 'clear-cooldown', payload);

export const disableAdminBreaker = (breakerId: string, payload: AdminBreakerActionRequest) =>
  postBreakerAction(breakerId, 'disable', payload);

export const reenableAdminBreaker = (breakerId: string, payload: AdminBreakerActionRequest) =>
  postBreakerAction(breakerId, 're-enable', payload);

export const getSystemResetStatus = () =>
  request<SystemResetStatus>('/api/v1/admin/system-reset');

export const runSystemReset = (payload: SystemResetRunRequest) =>
  request<SystemResetStatus>('/api/v1/admin/system-reset', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Get feasibility rejections
 */
export const getFeasibilityRejections = (period: string = '24h') => {
  const params = new URLSearchParams({ period });
  return request<FeasibilityRejectionsResponse>(`/api/v1/risk/feasibility-rejections?${params.toString()}`);
};

/**
 * Get list of tracked symbols from Redis price keys
 */
export const getMarketSymbols = () =>
  request<MarketSymbolsResponse>('/api/market/symbols');

/**
 * Get deterministic point-in-time Symbol Story evidence.
 */
export const getSymbolStory = (symbol: string, asOf?: string) => {
  const params = new URLSearchParams();
  if (asOf) params.append('as_of', asOf);
  const queryString = params.toString();
  return request<SymbolStoryResponse>(
    `/api/symbols/${encodeURIComponent(symbol)}/story${queryString ? `?${queryString}` : ''}`,
  );
};

/**
 * Get ADR-124 forecast-bound quality labels for a symbol.
 */
export const getForecastBoundQuality = (
  symbol: string,
  options?: { asOf?: string; lookbackDays?: number; limit?: number },
) => {
  const params = new URLSearchParams();
  if (options?.asOf) params.append('as_of', options.asOf);
  if (options?.lookbackDays) params.append('lookback_days', String(options.lookbackDays));
  if (options?.limit) params.append('limit', String(options.limit));
  const queryString = params.toString();
  return request<ForecastBoundQualityReport>(
    `/api/symbols/${encodeURIComponent(symbol)}/forecast-bound-quality${queryString ? `?${queryString}` : ''}`,
  );
};

/**
 * Get ADR-124 forecast-bound quality labels for specific signal_ids.
 */
export const getForecastBoundSignalsQuality = (
  signalIds: string[],
  options?: { limit?: number },
) => {
  const unique = Array.from(new Set(signalIds.map((id) => id.trim()).filter(Boolean)));
  const params = new URLSearchParams();
  params.append('signal_ids', unique.slice(0, options?.limit ?? 200).join(','));
  if (options?.limit) params.append('limit', String(options.limit));
  return request<ForecastBoundSignalsQualityReport>(
    `/api/signals/forecast-bound-quality?${params.toString()}`,
  );
};

/**
 * Get ADR-124 aggregate readiness for specific signal_ids.
 */
export const getForecastBoundSignalsAggregates = (
  signalIds: string[],
  options?: { lookbackDays?: number; limit?: number },
) => {
  const unique = Array.from(new Set(signalIds.map((id) => id.trim()).filter(Boolean)));
  const params = new URLSearchParams();
  params.append('signal_ids', unique.slice(0, options?.limit ?? 200).join(','));
  if (options?.lookbackDays) params.append('lookback_days', String(options.lookbackDays));
  if (options?.limit) params.append('limit', String(options.limit));
  return request<ForecastBoundSignalsAggregateReport>(
    `/api/signals/forecast-bound-quality/aggregates?${params.toString()}`,
  );
};

/**
 * Get ADR-124 label and aggregate readiness totals.
 */
export const getForecastBoundQualitySummary = (options?: { lookbackDays?: number }) => {
  const params = new URLSearchParams();
  if (options?.lookbackDays) params.append('lookback_days', String(options.lookbackDays));
  const queryString = params.toString();
  return request<ForecastBoundQualitySummaryReport>(
    `/api/forecast-bound-quality/summary${queryString ? `?${queryString}` : ''}`,
  );
};

/**
 * Get live ticker price for a symbol
 */
export const getMarketTicker = (symbol: string) =>
  request<MarketTickerResponse>(`/api/market/ticker/${encodeURIComponent(symbol)}`);

/**
 * Get OHLCV candles for a symbol from the market_data stream.
 * Backend returns { candles: [{ timestamp, open, high, low, close, volume }] }.
 * We normalise to { data: [{ time (unix seconds), open, high, low, close, volume }] }
 * so that the CandlestickChart component (which expects `response.data`) works correctly.
 */
export const getMarketCandles = async (
  symbol: string,
  interval: string = '1h',
  limit: number = 200,
): Promise<{ data: Array<{ time: number; open: number; high: number; low: number; close: number; volume: number }> }> => {
  const params = new URLSearchParams({ interval, limit: String(limit) });
  const raw = await request<{
    candles?: Array<{ timestamp: string; open: number; high: number; low: number; close: number; volume: number }>;
    data?: Array<{ time: number; open: number; high: number; low: number; close: number; volume: number }>;
  }>(`/api/market/candles/${encodeURIComponent(symbol)}?${params.toString()}`);

  // If backend already returns `data` array with numeric `time`, pass through.
  if (raw.data) return { data: raw.data };

  // Otherwise map from `candles` (ISO timestamp → unix seconds).
  const data = (raw.candles ?? []).map((c) => ({
    time: Math.floor(new Date(c.timestamp).getTime() / 1000),
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
  }));
  return { data };
};

/**
 * Get recent system fills for a symbol from Redis fills stream
 */
export const getMarketTrades = (symbol: string, limit: number = 20) => {
  const params = new URLSearchParams({ limit: String(limit) });
  return request<MarketTradesResponse>(`/api/market/trades/${encodeURIComponent(symbol)}?${params.toString()}`);
};

/**
 * Get activity feed
 */
export const getActivityFeed = (options?: {
  limit?: number;
  cursor?: string;
  event_types?: string;
}) => {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.cursor) params.append('cursor', options.cursor);
  if (options?.event_types) params.append('event_types', options.event_types);
  const queryString = params.toString();
  return request<ActivityFeedResponse>(`/api/dashboard/activity${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get agent tuning decisions from tuning.decisions stream.
 * NOTE: /agent/* routes live on metrics-api, not tradeclaw — must use metricsRequest().
 */
export const getAgentDecisions = (limit: number = 50) => {
  const params = new URLSearchParams({ limit: String(limit) });
  return metricsRequest<AgentDecisionsResponse>(`/agent/decisions?${params.toString()}`);
};

/**
 * Get agent status summary (total decisions, breaches, Cline spawns).
 * NOTE: /agent/* routes live on metrics-api, not tradeclaw — must use metricsRequest().
 */
export const getAgentStatus = () =>
  metricsRequest<AgentStatus>('/agent/status');

/**
 * Get ADR-129 self-learning quality review items from metrics-api.
 */
export const getAgentReviewItems = (options?: {
  limit?: number;
  status?: string;
  diagnosisType?: string;
}) => {
  const params = new URLSearchParams();
  params.set('limit', String(options?.limit ?? 50));
  if (options?.status) params.set('status', options.status);
  if (options?.diagnosisType) params.set('diagnosis_type', options.diagnosisType);
  return metricsRequest<AgentReviewItemsResponse>(`/agent/review-items?${params.toString()}`);
};

/**
 * Record an operator action for an ADR-129 review item.
 */
export const postAgentReviewItemAction = (
  reviewItemId: string,
  payload: { action: string; actor?: string; reason?: string },
) =>
  metricsRequest<AgentReviewItemActionResponse>(
    `/agent/review-items/${encodeURIComponent(reviewItemId)}/actions`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );

export const getModelRegistryStatus = (layers?: string[]) => {
  const params = new URLSearchParams();
  if (layers?.length) params.set('layers', layers.join(','));
  const queryString = params.toString();
  return metricsRequest<ModelRegistryStatusResponse>(
    `/metrics/model-registry/status${queryString ? `?${queryString}` : ''}`,
  );
};

export const getModelRollbackRequests = (limit: number = 25, targetLayer?: string) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (targetLayer) params.set('target_layer', targetLayer);
  return metricsRequest<ModelRollbackRequestsResponse>(
    `/metrics/model-registry/rollback-requests?${params.toString()}`,
  );
};

export const publishModelRollbackRequest = (payload: PublishModelRollbackRequestPayload) =>
  metricsRequest<PublishModelRollbackRequestResponse>('/metrics/model-registry/rollback-requests', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const getFastPolicyStatus = (options?: {
  limitPerFamily?: number;
  layers?: string[];
  families?: string[];
}) => {
  const params = new URLSearchParams();
  if (options?.limitPerFamily) params.set('limit_per_family', String(options.limitPerFamily));
  if (options?.layers?.length) params.set('layers', options.layers.join(','));
  if (options?.families?.length) params.set('families', options.families.join(','));
  const queryString = params.toString();
  return metricsRequest<FastPolicyStatusResponse>(
    `/metrics/fast-policy/status${queryString ? `?${queryString}` : ''}`,
  );
};

export const getServiceCleanupReadiness = () =>
  metricsRequest<ServiceCleanupReadinessResponse>('/metrics/service-cleanup-readiness');

export const getPtaCapitalBoundaryHealth = () =>
  metricsRequest<PtaCapitalBoundaryHealthResponse>('/metrics/pta/capital-boundary-health');

export const getPtaCutoverPreflight = () =>
  metricsRequest<PtaCutoverPreflightResponse>('/metrics/pta/cutover-preflight');

export const getPtaPromotionGapTargets = (limit: number = 5) =>
  metricsRequest<PtaPromotionGapTargetsResponse>(`/metrics/pta/promotion-gap-targets?limit=${limit}`);

export const getLearningProgress = (recentLimit: number = 250) =>
  metricsRequest<LearningProgressResponse>(`/metrics/learning-progress?recent_limit=${recentLimit}`);

// =============================================================================
// Metrics-API functions (paper trading state - port 18003)
// =============================================================================

/**
 * Get the combined metrics summary from metrics-api.
 * This is the primary endpoint for checking paper trading state.
 * Includes portfolio, performance, risk, and per-signal breakdowns.
 */
export const getMetricsSummary = () =>
  metricsRequest<MetricsSummaryData>('/metrics/summary');

/**
 * Get portfolio metrics (PnL, trades, balance, equity curve, tier)
 */
export const getMetricsPortfolio = () =>
  metricsRequest<MetricsPortfolioData>('/metrics/portfolio');

/**
 * Get performance metrics (Sharpe, win rate, profit factor)
 */
export const getMetricsPerformance = () =>
  metricsRequest<MetricsPerformanceData>('/metrics/performance');

/**
 * Get risk metrics (rolling drawdown, max drawdown, breaker tripped)
 */
export const getMetricsRisk = () =>
  metricsRequest<MetricsRiskData>('/metrics/risk');

export interface DecisionStoryResponse {
  position_id: string;
  [key: string]: unknown;
}

/**
 * Fetch post-mortem decision story by position id from metrics-api.
 */
export const getDecisionStory = (positionId: string) =>
  metricsRequest<DecisionStoryResponse>(
    `/api/decision-story?position_id=${encodeURIComponent(positionId)}`,
  );

export const useDecisionStory = (
  positionId?: string,
  options?: { enabled?: boolean; refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['decision-story', positionId ?? ''],
    queryFn: () => getDecisionStory(positionId ?? ''),
    enabled: Boolean(positionId) && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 30000,
    retry: 1,
  });

/**
 * Get feasibility service operational status.
 * NOTE: /feasibility/* lives on the feasibility service, not tradeclaw.
 * Uses metricsRequest (empty base URL) so the browser calls /feasibility/status
 * as a relative URL through the nginx reverse proxy.
 */
export const getFeasibilityStatus = () =>
  metricsRequest<FeasibilityStatusData>('/feasibility/status');

// =============================================================================
// Trading Mode Config API (metrics-api)
// =============================================================================

export interface TradingModeResponse {
  tier: 'paper' | 'live';
}

export interface SetTradingModeResponse {
  tier: 'paper' | 'live';
  ok: boolean;
}

export interface DirectionalBiasReplayGovernorEvent {
  entry_id: string;
  event: 'auto_replay_promote_cycle' | 'auto_replay_promote_run' | 'auto_replay_promote_dry_run';
  ts: string | null;
  trigger: string;
  status: string;
  dry_run: boolean;
  promotions_count: number;
  horizons_selected: number[];
  horizons_promoted: number[];
}

export interface DirectionalBiasReplayGovernorResponse {
  snapshot_ts: string;
  stream_key: string;
  cursor_key: string;
  cursor: number | null;
  audit_stream_length: number;
  events_scanned: number;
  events_matched: number;
  last_event: DirectionalBiasReplayGovernorEvent | null;
  last_background_cycle: DirectionalBiasReplayGovernorEvent | null;
  last_24h: {
    matched_events: number;
    background_cycles: number;
    promotions_total: number;
    status_counts: Record<string, number>;
  };
}

export interface PriceForecasterReplayGovernorEvent {
  entry_id: string;
  event: 'auto_replay_promote_cycle' | 'auto_replay_promote_run' | 'auto_replay_promote_dry_run';
  ts: string | null;
  trigger: string;
  status: string;
  dry_run: boolean;
  promotions_count: number;
  horizons_selected: number[];
  horizons_promoted: number[];
}

export interface PriceForecasterReplayGovernorResponse {
  snapshot_ts: string;
  stream_key: string;
  cursor_key: string;
  cursor: number | null;
  audit_stream_length: number;
  events_scanned: number;
  events_matched: number;
  last_event: PriceForecasterReplayGovernorEvent | null;
  last_background_cycle: PriceForecasterReplayGovernorEvent | null;
  last_24h: {
    matched_events: number;
    background_cycles: number;
    promotions_total: number;
    status_counts: Record<string, number>;
  };
}

export interface SignalFusionReplayGovernorEvent {
  entry_id: string;
  event:
    | 'auto_replay_promote_cycle'
    | 'auto_replay_promote_run'
    | 'auto_replay_promote_dry_run'
    | 'auto_replay_promote_guard_revert';
  ts: string | null;
  trigger: string;
  status: string;
  dry_run: boolean;
  candidate_id: string;
  candidate_fingerprint: string;
  candidate_score_delta: number | null;
  params_applied: string[];
}

export interface SignalFusionReplayGovernorResponse {
  snapshot_ts: string;
  stream_key: string;
  cursor_key: string;
  cursor: number | null;
  audit_stream_length: number;
  events_scanned: number;
  events_matched: number;
  last_event: SignalFusionReplayGovernorEvent | null;
  last_background_cycle: SignalFusionReplayGovernorEvent | null;
  last_24h: {
    matched_events: number;
    background_cycles: number;
    promotions_total: number;
    guard_reverts: number;
    status_counts: Record<string, number>;
  };
}

/**
 * GET current effective trading tier from metrics-api config endpoint.
 * Returns the runtime override if set, else the env-var-derived tier.
 */
export const getTradingMode = () =>
  metricsRequest<TradingModeResponse>('/metrics/config/trading-mode');

/**
 * GET directional-bias replay governor progress snapshot from metrics-api.
 */
export const getDirectionalBiasReplayGovernor = () =>
  metricsRequest<DirectionalBiasReplayGovernorResponse>('/metrics/directional-bias/replay-governor');

/**
 * GET price-forecaster replay governor progress snapshot from metrics-api.
 */
export const getPriceForecasterReplayGovernor = () =>
  metricsRequest<PriceForecasterReplayGovernorResponse>('/metrics/price-forecaster/replay-governor');

/**
 * GET signal-fusion replay governor progress snapshot from metrics-api.
 */
export const getSignalFusionReplayGovernor = () =>
  metricsRequest<SignalFusionReplayGovernorResponse>('/metrics/signal-fusion/replay-governor');

/**
 * POST to set the runtime trading tier override via metrics-api.
 * Persists the selection to Redis so it survives service restarts.
 */
export const setTradingMode = (tier: 'paper' | 'live') =>
  metricsRequest<SetTradingModeResponse>('/metrics/config/trading-mode', {
    method: 'POST',
    body: JSON.stringify({ tier }),
  });

// =============================================================================
// React Query Hooks
// =============================================================================

// Query keys
export const tradeClawKeys = {
  all: ['tradeclaw'] as const,
  status: () => [...tradeClawKeys.all, 'status'] as const,
  intents: () => [...tradeClawKeys.all, 'intents'] as const,
  positions: () => [...tradeClawKeys.all, 'positions'] as const,
  tradeMovie: (entityType: string, entityId: string) => [...tradeClawKeys.all, 'tradeMovie', entityType, entityId] as const,
  counterfactualResult: (jobId: string) => [...tradeClawKeys.all, 'counterfactualResult', jobId] as const,
  counterfactualEvents: (jobId: string) => [...tradeClawKeys.all, 'counterfactualEvents', jobId] as const,
  portfolio: () => [...tradeClawKeys.all, 'portfolio'] as const,
  portfolioHistory: (timeframe: string) => [...tradeClawKeys.all, 'portfolioHistory', timeframe] as const,
  pipeline: () => [...tradeClawKeys.all, 'pipeline'] as const,
  health: () => [...tradeClawKeys.all, 'health'] as const,
  opportunities: () => [...tradeClawKeys.all, 'opportunities'] as const,
  stats: () => [...tradeClawKeys.all, 'stats'] as const,
  accuracy: (period: string) => [...tradeClawKeys.all, 'accuracy', period] as const,
  eaEntryQualityReadiness: (maxCohorts: number, scopeType?: string) =>
    [...tradeClawKeys.all, 'eaEntryQualityReadiness', maxCohorts, scopeType] as const,
  executionQuality: () => [...tradeClawKeys.all, 'executionQuality'] as const,
  executionLatency: (venue?: string) => [...tradeClawKeys.all, 'executionLatency', venue] as const,
  riskExposure: () => [...tradeClawKeys.all, 'riskExposure'] as const,
  riskDrawdown: () => [...tradeClawKeys.all, 'riskDrawdown'] as const,
  riskStressTest: () => [...tradeClawKeys.all, 'riskStressTest'] as const,
  riskEvents: (options?: { severity?: string; category?: string }) => [...tradeClawKeys.all, 'riskEvents', options] as const,
  adminBreakers: (options?: { active_only?: boolean; limit?: number }) => [...tradeClawKeys.all, 'adminBreakers', options] as const,
  systemResetStatus: () => [...tradeClawKeys.all, 'systemResetStatus'] as const,
  feasibilityRejections: (period: string) => [...tradeClawKeys.all, 'feasibilityRejections', period] as const,
  activityFeed: (options?: { eventTypes?: string }) => [...tradeClawKeys.all, 'activityFeed', options] as const,
  marketSymbols: () => [...tradeClawKeys.all, 'marketSymbols'] as const,
  symbolStory: (symbol: string, asOf?: string) => [...tradeClawKeys.all, 'symbolStory', symbol, asOf] as const,
  forecastBoundQuality: (symbol: string, options?: { asOf?: string; lookbackDays?: number; limit?: number }) =>
    [...tradeClawKeys.all, 'forecastBoundQuality', symbol, options] as const,
  forecastBoundSignalsQuality: (signalIds: string[]) =>
    [...tradeClawKeys.all, 'forecastBoundSignalsQuality', signalIds] as const,
  forecastBoundSignalsAggregates: (signalIds: string[], lookbackDays?: number) =>
    [...tradeClawKeys.all, 'forecastBoundSignalsAggregates', signalIds, lookbackDays] as const,
  forecastBoundQualitySummary: (lookbackDays?: number) =>
    [...tradeClawKeys.all, 'forecastBoundQualitySummary', lookbackDays] as const,
  signalIntents: (options?: { verdict?: string; symbol?: string; signal_type?: string; fusion_recommended_only?: boolean; limit?: number }) => [...tradeClawKeys.all, 'signalIntents', options] as const,
  marketTicker: (symbol: string) => [...tradeClawKeys.all, 'marketTicker', symbol] as const,
  marketTrades: (symbol: string, limit?: number) => [...tradeClawKeys.all, 'marketTrades', symbol, limit] as const,
  agentDecisions: (limit?: number) => [...tradeClawKeys.all, 'agentDecisions', limit] as const,
  agentStatus: () => [...tradeClawKeys.all, 'agentStatus'] as const,
  modelRegistryStatus: (layers?: string[]) => [...tradeClawKeys.all, 'modelRegistryStatus', layers] as const,
  modelRollbackRequests: (limit?: number, targetLayer?: string) =>
    [...tradeClawKeys.all, 'modelRollbackRequests', limit, targetLayer] as const,
  fastPolicyStatus: (options?: { limitPerFamily?: number; layers?: string[]; families?: string[] }) =>
    [...tradeClawKeys.all, 'fastPolicyStatus', options] as const,
  ptaCapitalBoundaryHealth: () => [...tradeClawKeys.all, 'ptaCapitalBoundaryHealth'] as const,
  ptaCutoverPreflight: () => [...tradeClawKeys.all, 'ptaCutoverPreflight'] as const,
  ptaPromotionGapTargets: (limit?: number) => [...tradeClawKeys.all, 'ptaPromotionGapTargets', limit] as const,
  learningProgress: (recentLimit?: number) => [...tradeClawKeys.all, 'learningProgress', recentLimit] as const,
  serviceCleanupReadiness: () => [...tradeClawKeys.all, 'serviceCleanupReadiness'] as const,
  agentReviewItems: (options?: { limit?: number; status?: string; diagnosisType?: string }) =>
    [...tradeClawKeys.all, 'agentReviewItems', options] as const,
  ntFills: (limit?: number) => [...tradeClawKeys.all, 'ntFills', limit] as const,
  executedTrades: (limit?: number) => [...tradeClawKeys.all, 'executedTrades', limit] as const,
  closedPositions: (limit?: number) => [...tradeClawKeys.all, 'closedPositions', limit] as const,
  metricsSummary: () => [...tradeClawKeys.all, 'metricsSummary'] as const,
  metricsPortfolio: () => [...tradeClawKeys.all, 'metricsPortfolio'] as const,
  metricsPerformance: () => [...tradeClawKeys.all, 'metricsPerformance'] as const,
  metricsRisk: () => [...tradeClawKeys.all, 'metricsRisk'] as const,
  directionalBiasReplayGovernor: () => [...tradeClawKeys.all, 'directionalBiasReplayGovernor'] as const,
  priceForecasterReplayGovernor: () => [...tradeClawKeys.all, 'priceForecasterReplayGovernor'] as const,
  signalFusionReplayGovernor: () => [...tradeClawKeys.all, 'signalFusionReplayGovernor'] as const,
  feasibilityStatus: () => [...tradeClawKeys.all, 'feasibilityStatus'] as const,
  instrumentMode: () => [...tradeClawKeys.all, 'instrumentMode'] as const,
  executionGate: () => [...tradeClawKeys.all, 'executionGate'] as const,
  banditArms: () => [...tradeClawKeys.all, 'banditArms'] as const,
  attestations: (params?: { date?: string; chain?: string; status?: string }) => [...tradeClawKeys.all, 'attestations', params] as const,
  fusionSignals: (options?: {
    symbol?: string;
    recommended_only?: boolean;
    source?: 'all' | 'reactive' | 'proactive';
    view?: 'latest' | 'history' | 'combined';
    history_limit?: number;
    limit?: number;
    storage?: 'redis' | 'database' | 'all';
    since?: string;
    until?: string;
    signal_type?: string;
    direction?: 'long' | 'short';
    min_score?: number;
  }) => [...tradeClawKeys.all, 'fusionSignals', options] as const,
  attestationVerify: (date: string, signalId: string) => [...tradeClawKeys.all, 'attestationVerify', date, signalId] as const,
};

/**
 * Hook for TradeOS status
 */
export const useStatus = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.status(),
    queryFn: getStatus,
    refetchInterval: options?.refetchInterval ?? 5000,
    staleTime: 5000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook for pending intents
 */
export const usePendingIntents = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.intents(),
    queryFn: getPendingIntents,
    refetchInterval: options?.refetchInterval ?? 10000,
  });
};

/**
 * Hook for open positions
 */
export const usePositions = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.positions(),
    queryFn: getPositions,
    refetchInterval: options?.refetchInterval ?? 5000,
  });
};

export const useTradeMovie = (
  entityType?: TradeMovieEntityType | null,
  entityId?: string | null,
  options?: { enabled?: boolean; staleTime?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.tradeMovie(entityType ?? '', entityId ?? ''),
    queryFn: ({ signal }) => getTradeMovie(entityType as TradeMovieEntityType, entityId as string, signal),
    enabled: Boolean(entityType && entityId && (options?.enabled ?? true)),
    staleTime: options?.staleTime ?? 15000,
    retry: 1,
  });
};

export const useRunCounterfactual = () =>
  useMutation({
    mutationFn: (payload: CounterfactualRunRequest) => runCounterfactual(payload),
  });

export const useCounterfactualResult = (
  jobId?: string | null,
  running: boolean = false,
) => {
  return useQuery({
    queryKey: tradeClawKeys.counterfactualResult(jobId ?? ''),
    queryFn: ({ signal }) => getCounterfactualResult(jobId as string, signal),
    enabled: Boolean(jobId),
    refetchInterval: running ? 1500 : false,
    staleTime: running ? 0 : Infinity,
    retry: 1,
  });
};

export const useCounterfactualEvents = (
  jobId?: string | null,
  running: boolean = false,
) => {
  return useQuery({
    queryKey: tradeClawKeys.counterfactualEvents(jobId ?? ''),
    queryFn: ({ signal }) => getCounterfactualEvents(jobId as string, signal),
    enabled: Boolean(jobId),
    refetchInterval: running ? 1500 : false,
    staleTime: running ? 0 : 5000,
    retry: 1,
  });
};

/**
 * Hook for recently closed positions from positions.close stream with 30s polling.
 */
export const useClosedPositions = (limit: number = 50, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.closedPositions(limit),
    queryFn: () => getClosedPositions(limit),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });
};

/**
 * Hook for dashboard portfolio data
 */
export const useDashboardPortfolio = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.portfolio(),
    queryFn: getDashboardPortfolio,
    refetchInterval: options?.refetchInterval ?? 10000,
    staleTime: 5000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook for portfolio history with AbortController for rapid switching
 */
export const usePortfolioHistory = (
  timeframe: '24h' | '7d' | '30d' | '90d' | 'ytd' = '30d',
  options?: { refetchInterval?: number }
) => {
  return useQuery({
    queryKey: tradeClawKeys.portfolioHistory(timeframe),
    queryFn: ({ signal }) => getPortfolioHistory(timeframe, signal),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000, // Consider data fresh for 30s
  });
};

/**
 * Hook for pipeline health
 */
export const usePipelineHealth = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.pipeline(),
    queryFn: getPipelineHealth,
    refetchInterval: options?.refetchInterval ?? 30000,
  });
};

/**
 * Hook for detailed health
 */
export const useDetailedHealth = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.health(),
    queryFn: getDetailedHealth,
    refetchInterval: options?.refetchInterval ?? 60000,
  });
};

/**
 * Hook for directional-bias replay governor progress.
 */
export const useDirectionalBiasReplayGovernor = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.directionalBiasReplayGovernor(),
    queryFn: getDirectionalBiasReplayGovernor,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

/**
 * Hook for price-forecaster replay governor progress.
 */
export const usePriceForecasterReplayGovernor = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.priceForecasterReplayGovernor(),
    queryFn: getPriceForecasterReplayGovernor,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

/**
 * Hook for signal-fusion replay governor progress.
 */
export const useSignalFusionReplayGovernor = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.signalFusionReplayGovernor(),
    queryFn: getSignalFusionReplayGovernor,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

/**
 * Hook for scanner opportunities
 */
export const useScannerOpportunities = (limit?: number, symbol?: string) => {
  return useQuery({
    queryKey: [...tradeClawKeys.opportunities(), limit, symbol],
    queryFn: () => getScannerOpportunities(limit, symbol),
    refetchInterval: 10000,
    staleTime: 5000,
  });
};

/**
 * Hook for proposing intents
 */
export const useProposeIntent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: proposeIntent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.intents() });
    },
  });
};

/**
 * Hook for approving intents
 */
export const useApproveIntent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: approveIntent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.intents() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.positions() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
    },
  });
};

/**
 * Hook for rejecting intents
 */
export const useRejectIntent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rejectIntent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.intents() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
    },
  });
};

/**
 * Hook for kill switch
 */
export const useKillSwitch = () => {
  const queryClient = useQueryClient();
  
  const enable = useMutation({
    mutationFn: enableKillSwitch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
    },
  });
  
  const disable = useMutation({
    mutationFn: disableKillSwitch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
    },
  });
  
  return { enable, disable };
};

/**
 * Hook for assistant chat
 */
export const useAssistantChat = () => {
  return useMutation({
    mutationFn: assistantChat,
  });
};

/**
 * Hook for assistant execute
 */
export const useAssistantExecute = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assistantExecute,
    onSuccess: () => {
      // Invalidate all relevant queries after execution
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.all });
    },
  });
};

/**
 * Hook for signal accuracy data with 30s polling
 */
export const useSignalAccuracy = (period: string = '24h', options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.accuracy(period),
    queryFn: () => getSignalAccuracy(period),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000, // Consider data fresh for 10s
    retry: 2,
  });
};

/**
 * Hook for ADR-126 EA entry-quality promotion readiness.
 */
export const useEAEntryQualityReadiness = (
  maxCohorts = 50,
  scopeType?: string,
  options?: { refetchInterval?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.eaEntryQualityReadiness(maxCohorts, scopeType),
    queryFn: () => getEAEntryQualityReadiness(maxCohorts, scopeType),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 15000,
    retry: 2,
  });
};

/**
 * Hook for execution quality data with 60s polling
 */
export const useExecutionQuality = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.executionQuality(),
    queryFn: getExecutionQuality,
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 2,
  });
};

/**
 * Hook for execution latency data with 60s polling
 */
export const useExecutionLatency = (venue?: string, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.executionLatency(venue),
    queryFn: () => getExecutionLatency(venue),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 2,
  });
};

/**
 * Hook for risk exposure data with 30s polling
 */
export const useRiskExposure = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.riskExposure(),
    queryFn: getRiskExposure,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 2,
  });
};

/**
 * Hook for drawdown data with 30s polling
 */
export const useRiskDrawdown = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.riskDrawdown(),
    queryFn: getRiskDrawdown,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 2,
  });
};

/**
 * Hook to manually reset (acknowledge) a drawdown warning banner.
 * Calls POST /api/v1/risk/drawdown/reset which sets a Redis ack key (1-hour TTL).
 * On success, invalidates the riskDrawdown query so the banner disappears immediately.
 */
export const useResetDrawdownBreaker = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetDrawdownBreaker,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskExposure() });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'riskEvents'] });
    },
  });
};

/**
 * Hook to reset the execution daily-loss gate baseline.
 * Ensures Risk page breaker state refreshes immediately.
 */
export const useResetDailyLossGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resetDailyLossGate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskExposure() });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'riskEvents'] });
    },
  });
};

/**
 * Hook for stress test data with 5min polling
 */
export const useRiskStressTest = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.riskStressTest(),
    queryFn: getRiskStressTest,
    refetchInterval: options?.refetchInterval ?? 300000, // 5 minutes
    staleTime: 60000,
    retry: 2,
  });
};

/**
 * Hook for risk events with 30s polling
 */
export const useRiskEvents = (options?: {
  limit?: number;
  severity?: string;
  category?: string;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.riskEvents({ severity: options?.severity, category: options?.category }),
    queryFn: () => getRiskEvents({ limit: options?.limit, severity: options?.severity, category: options?.category }),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 2,
  });
};

export const useAdminBreakers = (options?: {
  active_only?: boolean;
  limit?: number;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.adminBreakers({ active_only: options?.active_only, limit: options?.limit }),
    queryFn: () => getAdminBreakers({ active_only: options?.active_only, limit: options?.limit }),
    refetchInterval: options?.refetchInterval ?? 15000,
    staleTime: 5000,
    retry: 2,
  });
};

export const useResetAdminBreakerCounter = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ breakerId, payload }: { breakerId: string; payload: AdminBreakerActionRequest }) =>
      resetAdminBreakerCounter(breakerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
    },
  });
};

export const useClearAdminBreakerCooldown = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ breakerId, payload }: { breakerId: string; payload: AdminBreakerActionRequest }) =>
      clearAdminBreakerCooldown(breakerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
    },
  });
};

export const useDisableAdminBreaker = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ breakerId, payload }: { breakerId: string; payload: AdminBreakerActionRequest }) =>
      disableAdminBreaker(breakerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
    },
  });
};

export const useReenableAdminBreaker = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ breakerId, payload }: { breakerId: string; payload: AdminBreakerActionRequest }) =>
      reenableAdminBreaker(breakerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
    },
  });
};

export const useSystemResetStatus = (options?: {
  enabled?: boolean;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.systemResetStatus(),
    queryFn: getSystemResetStatus,
    enabled: options?.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.status === 'running'
        ? 2000
        : options?.refetchInterval ?? 15000,
    staleTime: 1000,
    retry: 1,
  });
};

export const useRunSystemReset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runSystemReset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.systemResetStatus() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.status() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskExposure() });
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.riskDrawdown() });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'adminBreakers'] });
    },
  });
};

/**
 * Hook for feasibility rejections with 1min polling
 */
export const useFeasibilityRejections = (period: string = '24h', options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.feasibilityRejections(period),
    queryFn: () => getFeasibilityRejections(period),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 2,
  });
};

/**
 * Hook for activity feed with 30s polling
 */
export const useActivityFeed = (options?: {
  limit?: number;
  eventTypes?: string;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.activityFeed({ eventTypes: options?.eventTypes }),
    queryFn: () => getActivityFeed({ limit: options?.limit, event_types: options?.eventTypes }),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    placeholderData: keepPreviousData,
    retry: 2,
  });
};

/**
 * Hook for market symbols with 30s polling
 */
export const useMarketSymbols = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.marketSymbols(),
    queryFn: getMarketSymbols,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 2,
  });
};

/**
 * Hook for deterministic Symbol Story evidence.
 */
export const useSymbolStory = (
  symbol: string,
  options?: { asOf?: string; enabled?: boolean; refetchInterval?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.symbolStory(symbol, options?.asOf),
    queryFn: () => getSymbolStory(symbol, options?.asOf),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
    enabled: Boolean(symbol) && (options?.enabled ?? true),
  });
};

/**
 * Hook for ADR-124 forecast-bound quality labels.
 */
export const useForecastBoundQuality = (
  symbol: string,
  options?: { asOf?: string; lookbackDays?: number; limit?: number; enabled?: boolean; refetchInterval?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.forecastBoundQuality(symbol, {
      asOf: options?.asOf,
      lookbackDays: options?.lookbackDays,
      limit: options?.limit,
    }),
    queryFn: () => getForecastBoundQuality(symbol, {
      asOf: options?.asOf,
      lookbackDays: options?.lookbackDays,
      limit: options?.limit,
    }),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 1,
    enabled: Boolean(symbol) && (options?.enabled ?? true),
  });
};

/**
 * Hook for ADR-124 forecast-bound labels keyed by signal_id.
 */
export const useForecastBoundSignalsQuality = (
  signalIds: string[],
  options?: { enabled?: boolean; refetchInterval?: number; limit?: number },
) => {
  const unique = Array.from(new Set(signalIds.map((id) => id.trim()).filter(Boolean))).slice(
    0,
    options?.limit ?? 200,
  );
  return useQuery({
    queryKey: tradeClawKeys.forecastBoundSignalsQuality(unique),
    queryFn: () => getForecastBoundSignalsQuality(unique, { limit: options?.limit }),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 1,
    enabled: unique.length > 0 && (options?.enabled ?? true),
  });
};

/**
 * Hook for ADR-124 aggregate readiness keyed by signal_id.
 */
export const useForecastBoundSignalsAggregates = (
  signalIds: string[],
  options?: { enabled?: boolean; refetchInterval?: number; lookbackDays?: number; limit?: number },
) => {
  const unique = Array.from(new Set(signalIds.map((id) => id.trim()).filter(Boolean))).slice(
    0,
    options?.limit ?? 200,
  );
  return useQuery({
    queryKey: tradeClawKeys.forecastBoundSignalsAggregates(unique, options?.lookbackDays),
    queryFn: () =>
      getForecastBoundSignalsAggregates(unique, {
        lookbackDays: options?.lookbackDays,
        limit: options?.limit,
      }),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 1,
    enabled: unique.length > 0 && (options?.enabled ?? true),
  });
};

/**
 * Hook for ADR-124 global label and aggregate readiness totals.
 */
export const useForecastBoundQualitySummary = (
  options?: { enabled?: boolean; refetchInterval?: number; lookbackDays?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.forecastBoundQualitySummary(options?.lookbackDays),
    queryFn: () => getForecastBoundQualitySummary({ lookbackDays: options?.lookbackDays }),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 1,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook for live ticker price with 5s polling
 */
export const useMarketTicker = (symbol: string, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.marketTicker(symbol),
    queryFn: () => getMarketTicker(symbol),
    refetchInterval: options?.refetchInterval ?? 5000,
    staleTime: 2000,
    retry: 1,
    enabled: !!symbol,
  });
};

/**
 * Hook for recent system fills for a symbol with 10s polling
 */
export const useMarketTrades = (symbol: string, limit: number = 20, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.marketTrades(symbol, limit),
    queryFn: () => getMarketTrades(symbol, limit),
    refetchInterval: options?.refetchInterval ?? 10000,
    staleTime: 5000,
    retry: 1,
    enabled: !!symbol,
  });
};

/**
 * Get filtered (rejected) signals from signals.filtered stream
 */
export const getFilteredSignals = (options?: {
  limit?: number;
  reason?: string;
  signal_type?: string;
  symbol?: string;
}) => {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.reason) params.append('reason', options.reason);
  if (options?.signal_type) params.append('signal_type', options.signal_type);
  if (options?.symbol) params.append('symbol', options.symbol);
  const queryString = params.toString();
  return request<FilteredSignalsResponse>(`/api/signals/filtered${queryString ? `?${queryString}` : ''}`);
};

/**
 * Hook for filtered signals with 30s polling
 */
export const useFilteredSignals = (options?: {
  limit?: number;
  reason?: string;
  signal_type?: string;
  symbol?: string;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: [...tradeClawKeys.all, 'filteredSignals', options],
    queryFn: () => getFilteredSignals(options),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 2,
  });
};

/**
 * Get post-feasibility intents from the intents Redis stream.
 * Returns APPROVED intents by default unless a verdict filter is provided.
 */
export const getIntents = (options?: {
  verdict?: string;
  limit?: number;
  symbol?: string;
  signal_type?: string;
  cursor?: string;
  fusion_recommended_only?: boolean;
}) => {
  const params = new URLSearchParams();
  params.append('verdict', options?.verdict ?? 'APPROVED');
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.symbol) params.append('symbol', options.symbol);
  if (options?.signal_type) params.append('signal_type', options.signal_type);
  if (options?.cursor) params.append('cursor', options.cursor);
  if (options?.fusion_recommended_only) params.append('fusion_recommended_only', 'true');
  params.append('order', 'desc');
  return request<IntentsResponse>(`/api/signals/intents?${params.toString()}`);
};

/**
 * Hook for post-feasibility intents with 15s polling.
 */
export const useIntents = (options?: {
  verdict?: string;
  limit?: number;
  symbol?: string;
  signal_type?: string;
  fusion_recommended_only?: boolean;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.signalIntents({
      verdict: options?.verdict,
      symbol: options?.symbol,
      signal_type: options?.signal_type,
      fusion_recommended_only: options?.fusion_recommended_only,
      limit: options?.limit,
    }),
    queryFn: () => getIntents(options),
    refetchInterval: options?.refetchInterval ?? 15000,
    staleTime: 5000,
    retry: 2,
  });
};

/**
 * Get fusion-scored signals from signal-fusion service (pre-feasibility).
 * Reads signals.fused:latest:{symbol} Redis cache keys.
 * These are scored but NOT yet passed through feasibility gates.
 */
export const getFusionSignals = (options?: {
  symbol?: string;
  recommended_only?: boolean;
  source?: 'all' | 'reactive' | 'proactive';
  limit?: number;
  view?: 'latest' | 'history' | 'combined';
  history_limit?: number;
  storage?: 'redis' | 'database' | 'all';
  since?: string;
  until?: string;
  signal_type?: string;
  direction?: 'long' | 'short';
  min_score?: number;
}) => {
  const params = new URLSearchParams();
  if (options?.symbol) params.append('symbol', options.symbol);
  if (options?.recommended_only) params.append('recommended_only', 'true');
  if (options?.source && options.source !== 'all') params.append('source', options.source);
  if (options?.limit) params.append('limit', String(options.limit));
  if (options?.view) params.append('view', options.view);
  if (options?.history_limit) params.append('history_limit', String(options.history_limit));
  if (options?.storage) params.append('storage', options.storage);
  if (options?.since) params.append('since', options.since);
  if (options?.until) params.append('until', options.until);
  if (options?.signal_type) params.append('signal_type', options.signal_type);
  if (options?.direction) params.append('direction', options.direction);
  if (options?.min_score != null) params.append('min_score', String(options.min_score));
  params.append('order', 'desc');
  const qs = params.toString();
  return request<FusionSignalsResponse>(`/api/signals/fusion${qs ? `?${qs}` : ''}`);
};

/**
 * Hook for fusion-scored signals (pre-feasibility) with 15s polling.
 * Shows what the fusion engine is seeing BEFORE feasibility gates apply.
 * Compare to useIntents() to understand what gets approved vs filtered.
 */
export const useFusionSignals = (options?: {
  symbol?: string;
  recommended_only?: boolean;
  source?: 'all' | 'reactive' | 'proactive';
  limit?: number;
  view?: 'latest' | 'history' | 'combined';
  history_limit?: number;
  storage?: 'redis' | 'database' | 'all';
  since?: string;
  until?: string;
  signal_type?: string;
  direction?: 'long' | 'short';
  min_score?: number;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.fusionSignals({
      symbol: options?.symbol,
      recommended_only: options?.recommended_only,
      source: options?.source,
      view: options?.view,
      history_limit: options?.history_limit,
      limit: options?.limit,
      storage: options?.storage,
      since: options?.since,
      until: options?.until,
      signal_type: options?.signal_type,
      direction: options?.direction,
      min_score: options?.min_score,
    }),
    queryFn: () => getFusionSignals(options),
    refetchInterval: options?.refetchInterval ?? 15000,
    staleTime: 5000,
    retry: 2,
  });
};

/**
 * Hook for agent tuning decisions with 30s polling
 */
export const useAgentDecisions = (limit: number = 50, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.agentDecisions(limit),
    queryFn: () => getAgentDecisions(limit),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });
};

/**
 * Hook for agent status summary with 30s polling
 */
export const useAgentStatus = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.agentStatus(),
    queryFn: getAgentStatus,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });
};

export const useModelRegistryStatus = (
  layers?: string[],
  options?: { refetchInterval?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.modelRegistryStatus(layers),
    queryFn: () => getModelRegistryStatus(layers),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const useModelRollbackRequests = (
  limit: number = 25,
  targetLayer?: string,
  options?: { refetchInterval?: number },
) => {
  return useQuery({
    queryKey: tradeClawKeys.modelRollbackRequests(limit, targetLayer),
    queryFn: () => getModelRollbackRequests(limit, targetLayer),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const usePublishModelRollbackRequestMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishModelRollbackRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'modelRegistryStatus'] });
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'modelRollbackRequests'] });
    },
  });
};

export const useFastPolicyStatus = (
  options?: {
    limitPerFamily?: number;
    layers?: string[];
    families?: string[];
    refetchInterval?: number;
  },
) => {
  const queryOptions = {
    limitPerFamily: options?.limitPerFamily,
    layers: options?.layers,
    families: options?.families,
  };
  return useQuery({
    queryKey: tradeClawKeys.fastPolicyStatus(queryOptions),
    queryFn: () => getFastPolicyStatus(queryOptions),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const useServiceCleanupReadiness = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.serviceCleanupReadiness(),
    queryFn: getServiceCleanupReadiness,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const usePtaCapitalBoundaryHealth = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.ptaCapitalBoundaryHealth(),
    queryFn: getPtaCapitalBoundaryHealth,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const usePtaCutoverPreflight = (options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.ptaCutoverPreflight(),
    queryFn: getPtaCutoverPreflight,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const usePtaPromotionGapTargets = (options?: { limit?: number; refetchInterval?: number }) => {
  const limit = options?.limit ?? 5;
  return useQuery({
    queryKey: tradeClawKeys.ptaPromotionGapTargets(limit),
    queryFn: () => getPtaPromotionGapTargets(limit),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

export const useLearningProgress = (options?: { recentLimit?: number; refetchInterval?: number }) => {
  const recentLimit = options?.recentLimit ?? 250;
  return useQuery({
    queryKey: tradeClawKeys.learningProgress(recentLimit),
    queryFn: () => getLearningProgress(recentLimit),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 10000,
    retry: 1,
  });
};

/**
 * Hook for ADR-129 self-learning quality review items.
 */
export const useAgentReviewItems = (options?: {
  limit?: number;
  status?: string;
  diagnosisType?: string;
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: tradeClawKeys.agentReviewItems({
      limit: options?.limit,
      status: options?.status,
      diagnosisType: options?.diagnosisType,
    }),
    queryFn: () => getAgentReviewItems(options),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });
};

/**
 * Mutation for ADR-129 review item actions.
 */
export const useAgentReviewItemActionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      reviewItemId: string;
      action: string;
      actor?: string;
      reason?: string;
    }) =>
      postAgentReviewItemAction(input.reviewItemId, {
        action: input.action,
        actor: input.actor,
        reason: input.reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...tradeClawKeys.all, 'agentReviewItems'] });
    },
  });
};

/**
 * Hook for the combined metrics summary (paper trading state) with 30s polling.
 * trading_tier='paper' means the system is in simulation mode (NT_TIER=1).
 */
export const useMetricsSummary = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.metricsSummary(),
    queryFn: getMetricsSummary,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

/**
 * Hook for portfolio metrics (PnL, trades, balance, equity curve, tier) with 30s polling.
 */
export const useMetricsPortfolio = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.metricsPortfolio(),
    queryFn: getMetricsPortfolio,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });

/**
 * Hook for performance metrics (Sharpe, win rate, profit factor) with 30s polling.
 */
export const useMetricsPerformance = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.metricsPerformance(),
    queryFn: getMetricsPerformance,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });

/**
 * Hook for risk metrics (rolling drawdown, max drawdown, breaker tripped) with 30s polling.
 */
export const useMetricsRisk = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.metricsRisk(),
    queryFn: getMetricsRisk,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });

/**
 * Hook for feasibility service operational status with 30s polling.
 * Shows running state, fail-open/degraded mode, verdict distribution,
 * venue health, policy integrity, and more.
 */
export const useFeasibilityStatus = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.feasibilityStatus(),
    queryFn: getFeasibilityStatus,
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });

// =============================================================================
// Instrument Mode API (ADR-035 Phase 4.1)
// =============================================================================

export type InstrumentModeValue = 'futures_only' | 'spot_only';

export interface InstrumentModeResponse {
  instrument_mode: InstrumentModeValue;
  description: string;
  available_modes: Record<InstrumentModeValue, string>;
  changed_by?: string | null;
  changed_at?: string | null;
  previous_mode?: string | null;
}

export interface SetInstrumentModeRequest {
  instrument_mode: InstrumentModeValue;
}

/** GET /api/instrument-mode — returns current instrument mode from Redis */
export const getInstrumentMode = () =>
  request<InstrumentModeResponse>('/api/instrument-mode');

/** POST /api/instrument-mode — sets instrument mode in Redis */
export const setInstrumentMode = (instrument_mode: InstrumentModeValue) =>
  request<InstrumentModeResponse>('/api/instrument-mode', {
    method: 'POST',
    body: JSON.stringify({ instrument_mode } satisfies SetInstrumentModeRequest),
  });

/** Hook for reading current instrument mode with 60s polling */
export const useInstrumentMode = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.instrumentMode(),
    queryFn: getInstrumentMode,
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    retry: 1,
  });

/** Mutation hook for updating instrument mode */
export const useSetInstrumentMode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mode: InstrumentModeValue) => setInstrumentMode(mode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tradeClawKeys.instrumentMode() });
    },
  });
};

// ---------------------------------------------------------------------------
// Backtest types
// ---------------------------------------------------------------------------

export interface BacktestSignalInput {
  signal_id: string;
  symbol?: string;
  direction: 'long' | 'short';
  price?: number | null;
  strategy_id?: string;
  profile_id?: string;
  timestamp_ms?: number;
}

export interface BacktestFill {
  fill_id: string;
  signal_id: string;
  symbol: string;
  side: string;
  quantity: number;
  price: number;
  commission: number;
  strategy_id: string;
  timestamp_ms: number;
  pnl: number;
}

export interface BacktestMetrics {
  total_fills: number;
  total_pnl: number;
  win_rate: number;
  max_drawdown: number;
  avg_fill_price: number;
  total_commission: number;
  sharpe_ratio: number;
  gross_profit: number;
  gross_loss: number;
}

export interface BacktestResult {
  run_id: string;
  status: string;
  symbol: string;
  start_ts_ms: number;
  end_ts_ms: number;
  num_signals: number;
  fills: BacktestFill[];
  metrics: BacktestMetrics;
  error?: string | null;
  duration_ms: number;
  paper_trading: boolean;
}

export interface BacktestRunSummary {
  run_id: string;
  status: string;
  symbol: string;
  num_signals: number;
  total_pnl: number;
  win_rate: number;
  sharpe_ratio: number;
  duration_ms: number;
  start_ts_ms: number;
  end_ts_ms: number;
  created_at: number;
}

export interface BacktestRunRequest {
  signals: BacktestSignalInput[];
  start_ts_ms?: number | null;
  end_ts_ms?: number | null;
  account_balance?: number;
  commission_rate?: number;
  qty_per_trade?: number;
}

export interface BacktestRunsResponse {
  runs: BacktestRunSummary[];
  count: number;
}

// ---------------------------------------------------------------------------
// Backtest API functions
// ---------------------------------------------------------------------------

export const runBacktest = (req: BacktestRunRequest): Promise<BacktestResult> =>
  request<BacktestResult>('/api/backtest/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });

export const getBacktestRuns = (limit = 20): Promise<BacktestRunsResponse> =>
  request<BacktestRunsResponse>(`/api/backtest/runs?limit=${limit}`);

export const getBacktestResult = (run_id: string): Promise<BacktestResult> =>
  request<BacktestResult>(`/api/backtest/result/${run_id}`);

// ---------------------------------------------------------------------------
// Backtest hooks
// ---------------------------------------------------------------------------

export const useRunBacktest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: BacktestRunRequest) => runBacktest(req),
    onSuccess: () => {
      // Invalidate runs list so it refreshes after a new run
      queryClient.invalidateQueries({ queryKey: ['backtest', 'runs'] });
    },
  });
};

export const useBacktestRuns = (limit = 20, options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: ['backtest', 'runs', limit],
    queryFn: () => getBacktestRuns(limit),
    refetchInterval: options?.refetchInterval ?? 30_000,
    staleTime: 15_000,
    retry: 1,
  });

export const useBacktestResult = (run_id: string | null) =>
  useQuery({
    queryKey: ['backtest', 'result', run_id],
    queryFn: () => getBacktestResult(run_id!),
    enabled: !!run_id,
    staleTime: 300_000, // results don't change
    retry: 1,
  });

/**
 * Get executed trades from /api/trades.
 * Returns completed fills with id, orderId, symbol, side, quantity, price, fee, executedAt.
 */
export const getExecutedTrades = (limit: number = 50) => {
  const params = new URLSearchParams({ limit: String(limit) });
  return request<ExecutedTradesResponse>(`/api/trades?${params.toString()}`);
};

/**
 * Hook for executed trades (trade history / closed positions) with 30s polling.
 */
export const useExecutedTrades = (limit: number = 50, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.executedTrades(limit),
    queryFn: () => getExecutedTrades(limit),
    refetchInterval: options?.refetchInterval ?? 30000,
    staleTime: 15000,
    retry: 1,
  });
};

/**
 * Get recent NT-bridge fills from /api/dashboard/nt-fills (all symbols).
 * paper_trading_mode is true when NT_TIER=1 (venue=="SIM").
 */
export const getNtFills = (limit: number = 50) => {
  const params = new URLSearchParams({ limit: String(limit) });
  return request<NtFillsResponse>(`/api/dashboard/nt-fills?${params.toString()}`);
};

/**
 * Hook for NT-bridge fills with 10s polling.
 * Fetches fills across ALL symbols from the fills Redis stream.
 * paper_trading_mode=true indicates simulation mode (NT_TIER=1).
 */
export const useNtFills = (limit: number = 50, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: tradeClawKeys.ntFills(limit),
    queryFn: () => getNtFills(limit),
    refetchInterval: options?.refetchInterval ?? 10000,
    staleTime: 5000,
    retry: 1,
  });
};

// =============================================================================
// Position Monitor API (ADR-036 LiveSignalMonitor + ADR-038 TrailingStopOptimizer)
// =============================================================================

/**
 * GET /api/monitor/positions — per-position live signal scores and optimizer status.
 * Backed by LiveSignalMonitor (ADR-036) and TrailingStopOptimizer (ADR-038) Redis keys.
 */
export const getPositionMonitor = () =>
  request<PositionMonitorResponse>('/api/monitor/positions');

/**
 * Hook for live position monitor status (ADR-036 + ADR-038) with 15s polling.
 * Shows composite live_signal_score, component breakdown, last action taken,
 * and trailing stop / TP optimizer state for every open position.
 */
export const usePositionMonitor = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: [...tradeClawKeys.all, 'positionMonitor'],
    queryFn: getPositionMonitor,
    refetchInterval: options?.refetchInterval ?? 15000,
    staleTime: 8000,
    retry: 1,
  });

// =============================================================================
// ADR-041 Backtest Engine — types, API functions, hooks
// =============================================================================

/**
 * Base URL for the ADR-041 backtest engine.
 * Override via VITE_BACKTEST_ENGINE_URL env var; defaults to '' (same-origin nginx proxy).
 * nginx should forward /backtest/* → backtest service.
 */
const BACKTEST_ENGINE_URL = (import.meta.env.VITE_BACKTEST_ENGINE_URL as string) || '';

async function backtestEngineRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BACKTEST_ENGINE_URL}${endpoint}`;
  const defaultHeaders: HeadersInit = { 'Content-Type': 'application/json' };
  const token = useAuthStore.getState().token;
  if (token) defaultHeaders['Authorization'] = `Bearer ${token}`;

  const config: RequestInit = { ...options, headers: { ...defaultHeaders, ...options.headers } };
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), API_TIMEOUT);
  const mergedSignal = options.signal
    ? AbortSignal.any([options.signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const response = await fetch(url, { ...config, signal: mergedSignal });
    clearTimeout(timeoutId);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const msg = extractErrorMessage(errorData, `HTTP ${response.status}`);
      throw new TradeOSError(response.status, msg, errorData);
    }
    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof TradeOSError) throw error;
    if (error instanceof Error && error.name === 'AbortError') {
      if (options.signal?.aborted) throw error;
      throw new TradeOSError(408, 'Request timeout');
    }
    throw new TradeOSError(0, error instanceof Error ? error.message : 'Unknown error');
  }
}

/** Config submitted to ADR-041 backtest service */
export interface Adr041BacktestConfig {
  symbols: string[];
  /** ISO date YYYY-MM-DD */
  start_date: string;
  /** ISO date YYYY-MM-DD */
  end_date: string;
  initial_capital?: number;
  risk_per_trade_pct?: number;
  base_stop_loss_pct?: number;
  base_take_profit_pct?: number;
  strategy?: string;
  /** Policy profile id, e.g. 'default' | 'conservative' | 'balanced' | 'aggressive' */
  profile_id?: string;
  extra?: Record<string, unknown>;
}

/** Immediate response from POST /backtest/run */
export interface Adr041SubmitResponse {
  run_id: string;
  status: 'running' | 'completed';
  cache_hit: boolean;
}

export interface Adr041TradeRecord {
  run_id: string;
  symbol: string;
  strategy: string;
  direction: 'LONG' | 'SHORT';
  entry_ts: string;
  entry_price: number;
  exit_ts: string | null;
  exit_price: number | null;
  stop_loss_price: number;
  take_profit_price: number;
  slippage: number;
  qty: number;
  pnl: number;
  pnl_pct: number;
  status: string;
  bar_count: number;
}

export interface Adr041SymbolResult {
  run_id: string;
  symbol: string;
  trades: Adr041TradeRecord[];
  total_pnl: number;
  win_rate: number;
  trade_count: number;
  max_drawdown_pct: number;
  // Phase-2 enriched metrics (may be absent from older backends)
  sharpe_ratio?: number;
  sortino_ratio?: number;
  calmar_ratio?: number;
  data_coverage_pct?: number;
}

export interface Adr041Metrics {
  total_return_pct: number;
  annualized_return_pct: number;
  sharpe_ratio: number;
  sortino_ratio: number;
  calmar_ratio: number;
  max_drawdown_pct: number;
  max_drawdown_duration_bars: number;
  win_rate: number;
  profit_factor: number;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  avg_trade_pnl: number;
  avg_win_pct: number;
  avg_loss_pct: number;
  /** Fraction of bars with valid OHLCV data [0,1]; Phase-2 field */
  data_coverage_pct?: number;
}

/** Full result from GET /backtest/{run_id} */
export interface Adr041BacktestResult {
  run_id: string;
  config_hash: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  finished_at: string | null;
  symbols_processed: string[];
  per_symbol: Adr041SymbolResult[];
  /** Portfolio equity curve — one float per bar, starting at initial_capital */
  equity_curve: number[];
  /** Optional regime labels aligned with equity_curve bars */
  regime_labels?: string[];
  metrics: Adr041Metrics;
  error: string | null;
  // Stamped by frontend after submit response
  cache_hit?: boolean;
  submitted_config?: Adr041BacktestConfig;
}

// ---------------------------------------------------------------------------
// ADR-041 API functions
// ---------------------------------------------------------------------------

export const submitAdr041Backtest = (
  config: Adr041BacktestConfig,
  run_id?: string,
): Promise<Adr041SubmitResponse> =>
  backtestEngineRequest<Adr041SubmitResponse>('/backtest/run', {
    method: 'POST',
    body: JSON.stringify({ config, ...(run_id ? { run_id } : {}) }),
  });

export const getAdr041BacktestResult = (run_id: string): Promise<Adr041BacktestResult> =>
  backtestEngineRequest<Adr041BacktestResult>(`/backtest/${run_id}`);

export const getAdr041CachedResult = (config_hash: string): Promise<Adr041BacktestResult> =>
  backtestEngineRequest<Adr041BacktestResult>(`/backtest/cached/${config_hash}`);

// ---------------------------------------------------------------------------
// ADR-041 React Query hooks
// ---------------------------------------------------------------------------

/** Mutation that submits a backtest config and returns run_id + cache_hit */
export const useSubmitAdr041Backtest = () =>
  useMutation({ mutationFn: (cfg: Adr041BacktestConfig) => submitAdr041Backtest(cfg) });

/**
 * Poll GET /backtest/{run_id} every 2s while status=running, then stop.
 * Pass `running=true` while the job is in flight.
 */
export const useAdr041BacktestResult = (
  run_id: string | null,
  running: boolean,
) =>
  useQuery({
    queryKey: ['adr041', 'result', run_id],
    queryFn: () => getAdr041BacktestResult(run_id!),
    enabled: !!run_id,
    refetchInterval: running ? 2000 : false,
    staleTime: running ? 0 : Infinity,
    retry: 1,
  });

// =============================================================================
// Support Contact API (public — no auth required)
// =============================================================================

export interface SupportContactRequest {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export interface SupportContactResponse {
  message: string;
}

/**
 * Submit a public support contact form.
 * Calls POST /api/support/contact on metrics-api (no JWT required).
 * Rate-limited to 3 submissions per IP per hour on the backend.
 */
export const submitSupportRequest = (body: SupportContactRequest) =>
  metricsRequest<SupportContactResponse>('/api/support/contact', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * Mutation hook for the support contact form.
 */
export const useSubmitSupportRequest = () =>
  useMutation({ mutationFn: submitSupportRequest });

// ── ADR-063 / ADR-065: Composite Score Execution Gate ────────────────────

export interface ExecutionGateMetrics {
  intents_blocked_by_score: number;
  intents_total: number;
  blocked_rate: number;
  score_p25: number | null;
  score_p50: number | null;
  score_p75: number | null;
  effective_threshold: number;
  regime_current: string;
  session_pnl_r: number;
  snapshot_ts: string;
  data_age_ms: number | null;
}

export interface BanditArmStat {
  threshold: number;
  pulls: number;
  mean_reward: number;
  ucb_score: number;
  is_selected: boolean;
}

export interface BanditArmsResponse {
  regime: string;
  arms: BanditArmStat[];
  total_pulls: number;
  snapshot_ts: string;
}

export const fetchExecutionGateMetrics = (): Promise<ExecutionGateMetrics> =>
  request<ExecutionGateMetrics>('/metrics/execution-gate');

export const fetchBanditArms = (): Promise<BanditArmsResponse> =>
  request<BanditArmsResponse>('/metrics/bandit/arms');

export const useExecutionGateMetrics = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.executionGate(),
    queryFn: fetchExecutionGateMetrics,
    refetchInterval: options?.refetchInterval ?? 30_000,
    staleTime: 25_000,
  });

export const useBanditArms = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: tradeClawKeys.banditArms(),
    queryFn: fetchBanditArms,
    refetchInterval: options?.refetchInterval ?? 30_000,
    staleTime: 25_000,
  });

// ── On-Chain Attestations (T-NEW-527) ─────────────────────────────────────────

export interface AttestationRecord {
  id: string;
  attestation_date: string;
  attestation_domain?: string;
  domain?: string;
  source_table?: string | null;
  leaf_schema?: string | null;
  merkle_root: string | null;
  tx_hash: string | null;
  block_number: number | null;
  chain: string | null;
  contract_address: string | null;
  signal_count: number;
  status: 'local' | 'pending' | 'confirmed' | 'failed';
  on_chain_status?: 'verified' | 'mismatch' | 'missing' | 'unavailable' | 'disabled' | 'local_only' | null;
  on_chain_verified?: boolean | null;
  on_chain_root?: string | null;
  on_chain_error?: string | null;
  error_message: string | null;
  created_at: string | null;
  confirmed_at: string | null;
}

export interface AttestationsResponse {
  attestations: AttestationRecord[];
  total: number;
  limit: number;
  offset: number;
  snapshot_ts: string;
}

export interface AttestationVerifyResponse {
  verified: boolean;
  valid?: boolean;
  proof_verified?: boolean;
  on_chain_verified?: boolean | null;
  on_chain_status?: 'verified' | 'mismatch' | 'missing' | 'unavailable' | 'disabled' | 'local_only';
  on_chain_root?: string | null;
  on_chain_error?: string | null;
  reason?: string;
  merkle_root: string;
  tx_hash: string | null;
  block_number?: number | null;
  chain?: string | null;
  contract_address?: string | null;
  domain?: string;
  proof?: Array<{ sibling: string; direction: 'left' | 'right' }>;
  leaf_hash?: string;
  signal_data?: Record<string, unknown>;
  date: string;
  signal_id: string;
}

export const fetchAttestations = (params?: {
  date?: string;
  domain?: string;
  chain?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<AttestationsResponse> => {
  const p = new URLSearchParams();
  if (params?.date) p.append('date', params.date);
  if (params?.domain) p.append('domain', params.domain);
  if (params?.chain) p.append('chain', params.chain);
  if (params?.status) p.append('status', params.status);
  if (params?.limit != null) p.append('limit', String(params.limit));
  if (params?.offset != null) p.append('offset', String(params.offset));
  const qs = p.toString();
  return metricsRequest<AttestationsResponse>(`/attestations${qs ? `?${qs}` : ''}`);
};

export const fetchAttestationVerify = (
  date: string,
  signalId: string,
  domain?: string,
): Promise<AttestationVerifyResponse> => {
  const qs = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  return metricsRequest<AttestationVerifyResponse>(`/attestations/${date}/verify/${signalId}${qs}`);
};

export const useAttestations = (
  params?: { date?: string; domain?: string; chain?: string; status?: string; limit?: number; offset?: number },
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: tradeClawKeys.attestations(params),
    queryFn: () => fetchAttestations(params),
    refetchInterval: options?.refetchInterval ?? 60_000,
    staleTime: 30_000,
    retry: 1,
  });

export const useAttestationVerify = (
  date: string | null,
  signalId: string | null,
  domain?: string | null,
) =>
  useQuery({
    queryKey: tradeClawKeys.attestationVerify(date ?? '', `${domain ?? ''}:${signalId ?? ''}`),
    queryFn: () => fetchAttestationVerify(date!, signalId!, domain ?? undefined),
    enabled: !!date && !!signalId,
    staleTime: 300_000, // proofs don't change
    retry: 1,
  });

// ---------------------------------------------------------------------------
// Directional Bias
// ---------------------------------------------------------------------------

export interface DirectionalBiasResult {
  symbol: string;
  ts: string;
  horizon_hours: number;
  direction: -1 | 0 | 1;
  strength: number;
  confidence: number;
  price_bias: number;
  sentiment_bias: number;
  sources: Record<string, number | string>;
  confidence_delta: number | null;
}

export interface DirectionalBiasBatchResponse {
  ts: string;
  horizon_hours: number;
  results: Record<string, DirectionalBiasResult>;
  errors: Record<string, string>;
}

// Default symbol list for directional bias queries
const BIAS_SYMBOLS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'DOGE/USDT',
  'LINK/USDT', 'ADA/USDT', 'DOT/USDT', 'UNI/USDT', 'POL/USDT',
  'SUI/USDT', 'AVAX/USDT', 'APT/USDT', 'NEAR/USDT', 'FET/USDT',
  'INJ/USDT', 'ARB/USDT', 'OP/USDT', 'TIA/USDT', 'KTA/USDT', 'VVV/USDT',
];

// Map display labels → API horizon_hours integer values
const HORIZON_TO_HOURS: Record<string, number> = {
  '1h': 1,
  '4h': 4,
  '24h': 24,
  '3d': 72,
  '7d': 168,
  '30d': 720,
};

// Backend expects BTC-USD format; frontend uses BTC/USDT
const _biasSymbol = (sym: string) => sym.replace('/', '-');

const getDirectionalBias = async (
  symbols: string[] = BIAS_SYMBOLS,
  horizon: string = '24h',
): Promise<DirectionalBiasBatchResponse> => {
  const apiBase = import.meta.env.VITE_API_URL || '';
  const horizonHours = HORIZON_TO_HOURS[horizon] ?? 24;
  // Backend expects horizon_hours as integer and BTC-USD symbol format
  const params = `symbols=${encodeURIComponent(symbols.map(_biasSymbol).join(','))}&horizon_hours=${horizonHours}`;
  const res = await fetch(`${apiBase}/bias/batch?${params}`);
  if (!res.ok) throw new Error(`directional-bias HTTP ${res.status}`);
  return res.json();
};

export const useDirectionalBias = (
  symbols?: string[],
  options?: { refetchInterval?: number; horizon?: string },
) =>
  useQuery({
    queryKey: ['directional-bias', symbols ?? BIAS_SYMBOLS, options?.horizon ?? '24h'],
    queryFn: () => getDirectionalBias(symbols, options?.horizon ?? '24h'),
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

// ─── Directional Bias Calibration (ADR-076) ───────────────────────────────────

export interface DirectionalBiasAccuracy {
  signal_type: string;
  accuracy: number;
  /** Backend field name is "recent" (not "recent_accuracy") */
  recent: number;
  trend: number;
  total: number;
}

export interface DirectionalBiasCalibrationResponse {
  signals_tracked: number;
  total_samples: number;
  ready_for_calibration: boolean;
  signal_accuracies: Record<string, DirectionalBiasAccuracy>;
  overall_accuracy: number;
  calibration_mult: number;
  updated_at: string | null;
}

/**
 * Raw fetch for directional bias calibration — returns the full JSON from /bias/calibration.
 * Both useDirectionalBiasAccuracy and useDirectionalBiasAccuracyBySymbol share this
 * single fetch via the same React Query cache key; they each apply a `select` transform
 * to derive their own data shape.  This ensures only ONE HTTP request fires per period.
 */
const getDirectionalBiasCalibrationRaw = async (period: string = '24h') => {
  // Use relative URL so /bias/calibration routes through the dashboard nginx proxy
  // (VITE_API_URL points to tradeclaw which does not serve /bias/* endpoints)
  const params = new URLSearchParams({ period });
  const res = await fetch(`/bias/calibration?${params.toString()}`);
  if (!res.ok) throw new Error(`directional-bias calibration HTTP ${res.status}`);
  return res.json();
};

/** Shared React Query key for the raw calibration endpoint (deduplicates requests) */
const biasCalibrationKey = (period: string) =>
  [...tradeClawKeys.all, 'directionalBiasCalibration', period] as const;

/** Period string → hours fallback map (mirrors backend _PERIOD_HOURS) */
const PERIOD_HOURS: Record<string, number> = { '1h': 1, '4h': 4, '24h': 24, '3d': 72, '7d': 168, '30d': 720 };

/**
 * Transform directional-bias calibration data into SignalAccuracy format.
 * periodHours: the actual window used by the backend for this query (from data.period_hours).
 * This ensures window_start correlates with the selected period rather than being hardcoded.
 */
function transformCalibrationToSignalAccuracy(
  data: {
    calibration?: {
      signals_tracked: number;
      total_samples: number;
      ready_for_calibration: boolean;
      signal_accuracies: Record<string, { accuracy: number; recent: number; trend: number; total: number }>;
    };
    latest_result?: {
      overall_accuracy: number;
      calibration_mult: number;
      updated_at: string;
    };
    period?: string;
    period_hours?: number;
  },
  periodHours?: number,
): { accuracy: SignalAccuracy[] } {
  const calibration = data.calibration;
  const latest = data.latest_result;

  if (!calibration || !calibration.signal_accuracies || Object.keys(calibration.signal_accuracies).length === 0) {
    return { accuracy: [] };
  }

  // Derive window boundaries from the actual period returned by the backend.
  // Fall back to PERIOD_HOURS[data.period] then to periodHours param, then to 720h (30d).
  const resolvedHours = periodHours ?? PERIOD_HOURS[data.period ?? ''] ?? 720;
  const windowEnd = new Date();
  const windowStart = new Date(windowEnd.getTime() - resolvedHours * 60 * 60 * 1000);

  const signals: SignalAccuracy[] = Object.entries(calibration.signal_accuracies).map(
    ([signal_type, acc]) => {
      const winning = Math.round(acc.recent * Math.min(acc.total, 100));
      return {
        signal_type,
        total_signals: acc.total,
        winning_signals: winning,
        losing_signals: acc.total - winning,
        win_rate: acc.accuracy,
        avg_pnl_bps: 0, // Not available from calibration
        total_pnl_bps: 0,
        avg_pnl_usd: null,
        total_pnl_usd: 0,
        sharpe_ratio: acc.trend !== 0 ? acc.trend * 2 : null, // Approximate Sharpe from trend
        profit_factor: acc.accuracy > 0 ? acc.accuracy / (1 - acc.accuracy + 0.001) : 1,
        max_drawdown_pct: 0,
        avg_holding_hours: 0,
        confidence: acc.recent,
        last_signal_at: latest?.updated_at || windowEnd.toISOString(),
        window_start: windowStart.toISOString(),
        window_end: windowEnd.toISOString(),
        avg_win_bps: 0,
        avg_loss_bps: 0,
        ic_value: null,
        ic_sample_count: acc.total,
        calibration_status: calibration.ready_for_calibration ? 'active' : 'cold_start',
      } satisfies SignalAccuracy;
    }
  );

  return { accuracy: signals };
}

export const useDirectionalBiasAccuracy = (period: string = '24h', options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: biasCalibrationKey(period),
    queryFn: () => getDirectionalBiasCalibrationRaw(period),
    select: (data) => transformCalibrationToSignalAccuracy(data, data.period_hours),
    refetchInterval: options?.refetchInterval ?? 60000,
    // staleTime must be strictly less than refetchInterval so React Query treats
    // the data as stale when the next poll fires and actually re-renders the UI.
    staleTime: 30000,
    placeholderData: keepPreviousData,
    retry: 2,
  });

export const useDirectionalBiasAccuracyBySymbol = (
  period: string = '24h',
  options?: { refetchInterval?: number },
) =>
  useQuery({
    // Same key as useDirectionalBiasAccuracy — React Query deduplicates the HTTP request.
    queryKey: biasCalibrationKey(period),
    queryFn: () => getDirectionalBiasCalibrationRaw(period),
    select: (data) => {
      const perSymbol: Record<string, { accuracy: number; correct: number; total: number }> =
        data?.calibration?.per_symbol ?? {};
      const symbols: SymbolBiasAccuracy[] = Object.entries(perSymbol)
        .map(([symbol, s]) => ({ symbol, accuracy: s.accuracy, correct: s.correct, total: s.total }))
        .sort((a, b) => b.total - a.total);
      return { symbols, period };
    },
    refetchInterval: options?.refetchInterval ?? 60000,
    staleTime: 30000,
    placeholderData: keepPreviousData,
    retry: 2,
  });

// ─── Price Forecaster (ADR-080 Phase 2A) ────────────────────────────────────

export interface PriceForecast {
  symbol: string;
  timeframe: string;
  forecast_at: string;
  model_version: string;
  expected_return: number;
  expected_return_effective?: number;
  expected_return_raw?: number;
  return_sigma: number;
  conf_raw?: number;
  confidence: number;
  gp_approved: number;
  gp_n_train: number;
  used_surrogate: number;
  forecast_path?: string;
  regime: string;
  regime_confidence: number;
  price_at_forecast: number;
  price_expected: number;
  price_expected_raw?: number;
  price_low_68: number;
  price_low_68_raw?: number;
  price_high_68: number;
  price_high_68_raw?: number;
  price_low_95: number;
  price_low_95_raw?: number;
  price_high_95: number;
  price_high_95_raw?: number;
  vol_forecast_1h: number;
  vol_forecast_4h: number;
  vol_forecast_24h: number;
  annualized_vol: number;
}

const getPriceForecasts = (timeframe: string): Promise<PriceForecast[]> =>
  request<PriceForecast[]>(`/forecasts?timeframe=${encodeURIComponent(timeframe)}`);

export const usePriceForecast = (
  timeframe: string = '1h',
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['price-forecasts', timeframe],
    queryFn: () => getPriceForecasts(timeframe),
    refetchInterval: options?.refetchInterval ?? 120_000,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

// ─── Forecast Accuracy / Evaluation (ADR-087 Phase 5) ─────────────────────────

export interface ForecastEvaluation {
  symbol: string;
  horizon: string;
  days: number;
  n_samples: number;
  hit_ratio: number;
  calibration_error: number;
  mae: number;
  rmse: number;
  kelly_fraction: number;
  sharpe_equivalent: number;
  regime_hit_ratios: Record<string, { hit_ratio: number; n: number }>;
  generated_at: string;
}

export interface ForecastAccuracyResponse {
  symbol: string;
  horizon: string;
  hit_ratio: number;
  calibration_error: number;
  mae: number;
  rmse: number;
  kelly_fraction: number;
  sharpe_equivalent: number;
  regime_hit_ratios?: Record<string, { hit_ratio: number; n: number }>;
  n_samples?: number;
  days?: number;
  generated_at?: string;
}

export interface ForecastAccuracySummary {
  horizon: string;
  days: number;
  status: 'ok' | 'insufficient_data' | string;
  data_source?: string;
  metric_population?: string;
  published_samples: number;
  total_samples?: number;
  all_samples?: number;
  resolved_samples: number;
  abstained_samples?: number;
  actionable_coverage: number;
  path_aware_samples?: number;
  legacy_samples?: number;
  path_aware_coverage?: number;
  published_accuracy: number | null;
  overall_hit_ratio: number | null;
  all_directional_accuracy?: number | null;
  overall_avg_confidence?: number | null;
  symbols_covered?: number;
  symbols_total?: number;
  generated_at?: string;
}

const getForecastEvaluation = async (
  symbol: string,
  horizon: string = '1d',
  days: number = 30,
): Promise<ForecastAccuracyResponse> => {
  const params = new URLSearchParams({
    symbol,
    horizon,
    days: String(days),
  });
  return request<ForecastAccuracyResponse>(`/forecasts/${encodeURIComponent(symbol)}/evaluate?${params.toString()}`);
};

const getForecastAccuracySummary = async (
  horizon: string = '24h',
  days: number = 30,
): Promise<ForecastAccuracySummary> => {
  const params = new URLSearchParams({
    horizon,
    days: String(days),
  });
  return request<ForecastAccuracySummary>(`/accuracy?${params.toString()}`);
};

export const useForecastEvaluation = (
  symbol: string = 'BTC',
  horizon: string = '1d',
  days: number = 30,
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['forecast-evaluation', symbol, horizon, days],
    queryFn: () => getForecastEvaluation(symbol, horizon, days),
    refetchInterval: options?.refetchInterval ?? 300_000, // 5 min — accuracy metrics don't change fast
    staleTime: 180_000,
    placeholderData: keepPreviousData,
    retry: 2,
  });

export const useForecastAccuracySummary = (
  horizon: string = '24h',
  days: number = 30,
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['forecast-accuracy-summary', horizon, days],
    queryFn: () => getForecastAccuracySummary(horizon, days),
    refetchInterval: options?.refetchInterval ?? 300_000,
    staleTime: 180_000,
    placeholderData: keepPreviousData,
    retry: 2,
  });

// ── ADR-085: AI Trading Chart — CH OHLCV endpoint ────────────────────────────

export interface OHLCVBar {
  ts: number;  // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OHLCVCHResponse {
  bars: OHLCVBar[];
  ema20: number[];
  ema50: number[];
  rsi14: number[];
  symbol: string;
  timeframe: string;
  count: number;
  snapshot_ts: string;
}

/**
 * Fetch historical OHLCV bars with EMA-20, EMA-50, RSI-14 from ClickHouse.
 * Endpoint: GET /api/market-data/ohlcv/ch/{symbol}?timeframe=1h&limit=200
 */
export const getOHLCVCH = (symbol: string, timeframe = '1h', limit = 200): Promise<OHLCVCHResponse> => {
  const params = new URLSearchParams({ timeframe, limit: String(limit) });
  return metricsRequest<OHLCVCHResponse>(
    `/api/market-data/ohlcv/ch/${encodeURIComponent(symbol)}?${params.toString()}`
  );
};

/** React Query hook for ADR-085 CH OHLCV data, auto-refreshes every 60s */
export const useOHLCVCH = (
  symbol: string,
  timeframe = '1h',
  limit = 200,
  options?: { refetchInterval?: number },
) =>
  useQuery({
    queryKey: ['ohlcv-ch', symbol, timeframe, limit],
    queryFn: () => getOHLCVCH(symbol, timeframe, limit),
    refetchInterval: options?.refetchInterval ?? 60_000,
    staleTime: 30_000,
    enabled: !!symbol,
    placeholderData: keepPreviousData,
    retry: 1,
  });

// ── ADR-085 Phase 4: BOCD + Hawkes types and hooks ────────────────────────────

export interface BOCDMetrics {
  p_changepoint: number;
  run_length_mode: number;
  run_length_mean: number;
  is_changepoint: boolean;
  signal_gate_open: boolean;
  kelly_scalar: number;
  t: number;
  symbol: string;
  last_updated: string | null;
}

const getBOCDMetrics = (): Promise<BOCDMetrics> =>
  metricsRequest<BOCDMetrics>('/metrics/regime/bocd');

export const useBOCDMetrics = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: ['bocd-metrics'],
    queryFn: getBOCDMetrics,
    refetchInterval: options?.refetchInterval ?? 30_000,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

export interface HawkesSymbolMetrics {
  symbol: string;
  intensity_buy: number;
  intensity_sell: number;
  ofi: number;
  branching_ratio: number;
  buy_count: number;
  sell_count: number;
  is_excited: boolean;
}

export interface HawkesOrderFlowMetrics {
  symbols: HawkesSymbolMetrics[];
  dominant_direction: string;
  avg_ofi: number;
  excited_symbols: string[];
  last_updated: string | null;
}

const getHawkesMetrics = (): Promise<HawkesOrderFlowMetrics> =>
  metricsRequest<HawkesOrderFlowMetrics>('/metrics/order-flow/hawkes');

export const useHawkesMetrics = (options?: { refetchInterval?: number }) =>
  useQuery({
    queryKey: ['hawkes-metrics'],
    queryFn: getHawkesMetrics,
    refetchInterval: options?.refetchInterval ?? 15_000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
    retry: 1,
  });

// Export all
export const tradeClawApi = {
  getStatus,
  getPendingIntents,
  proposeIntent,
  approveIntent,
  rejectIntent,
  enableKillSwitch,
  disableKillSwitch,
  getPositions,
  getStats,
  getPipelineHealth,
  getDetailedHealth,
  getScannerOpportunities,
  assistantChat,
  assistantChatStream,
  getAssistantReportJob,
  assistantExecute,
};

export { TradeOSError };
export default tradeClawApi;
